// This client was automatically generated by Segment Typewriter. ** Do Not Edit **
// To update this file, run:
//   npx typewriter

/**
 * SamaCare account is created. The subject of these properties should be the account being
 * created, not the account performing the action.
 */
export interface AccountCreated {
    /**
     * Specifies the email address of the account that is the target of the action, distinct
     * from the account initiating the action.
     */
    accountEmail: string;
    /**
     * specifies the ID of the account that is the target of the action, distinct from the
     * account initiating the action.
     */
    accountId:             number;
    institutionId:         number;
    institutionIsTop?:     boolean;
    institutionName?:      string;
    institutionSpecialty?: string;
    isAdmin:               boolean;
    isReadOnly:            boolean;
    isSamaUser:            boolean;
    [property: string]: any;
}

/**
 * SamaCare account is deactivated. The subject of these properties should be the account
 * being deactivated, not the account performing the action.
 */
export interface AccountDeactivated {
    /**
     * Specifies the email address of the account that is the target of the action, distinct
     * from the account initiating the action.
     */
    accountEmail: string;
    /**
     * specifies the ID of the account that is the target of the action, distinct from the
     * account initiating the action.
     */
    accountId:             number;
    institutionId:         number;
    institutionIsTop?:     boolean;
    institutionName?:      string;
    institutionSpecialty?: string;
    isAdmin:               boolean;
    isReadOnly:            boolean;
    isSamaUser:            boolean;
    [property: string]: any;
}

/**
 * SamaCare account is unlocked. The subject of these properties should be the account being
 * unlocked, not the account performing the action.
 */
export interface AccountUnlocked {
    /**
     * Specifies the email address of the account that is the target of the action, distinct
     * from the account initiating the action.
     */
    accountEmail: string;
    /**
     * specifies the ID of the account that is the target of the action, distinct from the
     * account initiating the action.
     */
    accountId:             number;
    institutionId:         number;
    institutionIsTop?:     boolean;
    institutionName?:      string;
    institutionSpecialty?: string;
    isAdmin:               boolean;
    isReadOnly:            boolean;
    isSamaUser:            boolean;
    [property: string]: any;
}

/**
 * Attempt an autofill on extension
 */
export interface ExtensionAutofillAttempt {
    /**
     * Extension input field key
     */
    fieldKey: string;
    /**
     * Input field xpath
     */
    fieldXPath:           string;
    institutionId:        number;
    institutionName:      string;
    institutionSpecialty: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    /**
     * Internal defined page key
     */
    pageKey?: string;
    paType:   PaType;
    /**
     * Portal ID
     */
    portalId: number;
    /**
     * Portal slug/key
     */
    portalTitle: string;
    /**
     * Url of the current page
     */
    url: string;
    /**
     * User’s current version of extension
     */
    version: string;
    [property: string]: any;
}

export enum PaType {
    Edi = "edi",
    Enrollment = "enrollment",
    External = "external",
    Form = "form",
    MultiParty = "multi_party",
    Portal = "portal",
    PortalExternal = "portal_external",
    Referral = "referral",
}

/**
 * Successfully detected autofill field on web extension
 */
export interface ExtensionAutofillFieldDetected {
    /**
     * Whether display autofill icon
     */
    autofillEnabled?: boolean;
    /**
     * Extension input field key
     */
    fieldKey: null | string;
    /**
     * Input field xpath
     */
    fieldXPath:            null | string;
    institutionId:         number | null;
    institutionName:       null | string;
    institutionSpecialty?: null | string;
    /**
     * ID of the PA
     */
    internalAuthId: number | null;
    /**
     * Internal defined page key
     */
    pageKey?: string;
    paType:   PaType;
    /**
     * Portal ID
     */
    portalId: number;
    /**
     * Portal slug/key
     */
    portalTitle: string;
    /**
     * Url of the current page that
     */
    url: string;
    /**
     * User’s current version of extension
     */
    version: string;
    [property: string]: any;
}

/**
 * User trying to override the value extension autofilled
 */
export interface ExtensionAutofillOverridden {
    /**
     * Extension input field key
     */
    fieldKey: null | string;
    /**
     * The value user override
     */
    fieldValueNew?: string;
    /**
     * The value extension trying to autofill
     */
    fieldValueOld: string;
    /**
     * Input field xpath
     */
    fieldXPath:            null | string;
    institutionId:         number | null;
    institutionName:       null | string;
    institutionSpecialty?: null | string;
    /**
     * ID of the PA
     */
    internalAuthId: number | null;
    /**
     * Internal defined page key
     */
    pageKey?: string;
    paType:   PaType;
    /**
     * Portal ID
     */
    portalId: number;
    /**
     * Portal slug/key
     */
    portalTitle: string;
    /**
     * Url of the current page that
     */
    url: string;
    /**
     * User’s current version of extension
     */
    version: string;
    [property: string]: any;
}

/**
 * Autofill value stored in PA to extension input field
 */
export interface ExtensionFieldAutofilled {
    errorKind: ErrorKind | null;
    /**
     * Extension input field key
     */
    fieldKey: string;
    /**
     * Input field value from authorization config
     */
    fieldValue: null | string;
    /**
     * Input field xpath
     */
    fieldXPath:           string;
    institutionId:        number;
    institutionName:      string;
    institutionSpecialty: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    /**
     * Internal defined page key
     */
    pageKey?: string;
    paType:   PaType;
    /**
     * Portal ID
     */
    portalId: number;
    /**
     * Portal slug/key
     */
    portalTitle: string;
    /**
     * whether the autofill is successfully filled
     */
    success: boolean;
    url:     string;
    /**
     * User’s current version of extension
     */
    version: string;
    [property: string]: any;
}

export enum ErrorKind {
    NoValue = "NO_VALUE",
    Unknown = "UNKNOWN",
}

/**
 * User views Extension (sidebar) form.
 */
export interface ExtensionFormViewed {
    /**
     * HCPCS code of value from Extension's HCPCS field
     */
    fieldHcpcsValue?: string;
    /**
     * True if Extension's New PA radio field is selected
     */
    fieldIsNewValue?: boolean;
    /**
     * Patient ID of value from Extension's Patient field
     */
    fieldPatientIdValue?: string;
    /**
     * Value from Extension's Payor field
     */
    fieldPayorValue?: string;
    /**
     * Value from Extension's Portal Reference ID field
     */
    fieldPortalAuthReferenceId?: string;
    /**
     * Value from Extension's Status field
     */
    fieldStatusValue?:     string;
    institutionId:         number;
    institutionIsTop?:     boolean;
    institutionName?:      string;
    institutionSpecialty?: string;
    openSource?:           OpenSource | null;
    /**
     * The ID of the portal
     */
    portalId?: number;
    /**
     * Portal's slug/key
     */
    portalKey?: string;
    /**
     * Portal's URL
     */
    portalOrigin?: string;
    /**
     * User’s current version of extension
     */
    version?: string;
    [property: string]: any;
}

export enum OpenSource {
    Autodetection = "autodetection",
    NavbarIcon = "navbar_icon",
    Widget = "widget",
}

/**
 * Extension detect a new input on screen
 */
export interface ExtensionInputFound {
    /**
     * ID of the found field if there is one
     */
    fieldInputId?: string;
    /**
     * name of the found field if there is one
     */
    fieldInputName?: string;
    /**
     * XPath of the found field
     */
    fieldXPath:           string;
    institutionId:        number;
    institutionName:      string;
    institutionSpecialty: string;
    internalAuthId:       number;
    paType:               PaType;
    /**
     * Portal ID
     */
    portalId: number;
    /**
     * Portal slug/key
     */
    portalTitle: string;
    /**
     * URL of the page the input is found on
     */
    url:     string;
    version: string;
    [property: string]: any;
}

/**
 * Extension is launched in user's browser.
 */
export interface ExtensionOpened {
    institutionId?:        number;
    institutionIsTop?:     boolean;
    institutionName?:      string;
    institutionSpecialty?: string;
    /**
     * Source of how extension was opened
     */
    openSource: OpenSource | null;
    /**
     * The ID of the portal
     */
    portalId?: number;
    /**
     * Portal's slug/key
     */
    portalKey?: string;
    /**
     * Portal's URL
     */
    portalOrigin?: string;
    /**
     * User’s current version of extension
     */
    version?: string;
    [property: string]: any;
}

/**
 * User performs CTA to save PA to SamaCare
 */
export interface ExtensionSaveToSamaCareButtonClicked {
    /**
     * HCPCS code of value from Extension's HCPCS field
     */
    fieldHcpcsValue?: string;
    /**
     * True if Extension's New PA radio field is selected
     */
    fieldIsNewValue?: boolean;
    /**
     * Patient ID of value from Extension's Patient field
     */
    fieldPatientIdValue?: string;
    /**
     * Value from Extension's Payor field
     */
    fieldPayorValue?: string;
    /**
     * Value from Extension's Portal Reference ID field
     */
    fieldPortalAuthReferenceId?: string;
    /**
     * Value from Extension's Status field
     */
    fieldStatusValue?:     string;
    institutionId:         number;
    institutionIsTop?:     boolean;
    institutionName?:      string;
    institutionSpecialty?: string;
    /**
     * The ID of the portal
     */
    portalId?: number;
    /**
     * Portal's slug/key
     */
    portalKey?: string;
    /**
     * Portal's URL
     */
    portalOrigin?: string;
    /**
     * User’s current version of extension
     */
    version?: string;
    [property: string]: any;
}

/**
 * User performs CTA to redirect to web app to view PA.
 */
export interface ExtensionViewAuthorizationButtonClicked {
    institutionId:         number;
    institutionIsTop?:     boolean;
    institutionName?:      string;
    institutionSpecialty?: string;
    /**
     * The ID of the portal
     */
    portalId?: number;
    /**
     * Portal's slug/key
     */
    portalKey?: string;
    /**
     * Portal's URL
     */
    portalOrigin?: string;
    /**
     * User’s current version of extension
     */
    version?: string;
    [property: string]: any;
}

/**
 * Create BV lite from unified BV check
 */
export interface InitiateBvLite {
    /**
     * ID associated with user account
     */
    accountId:     number;
    drugOptionId?: number | null;
    /**
     * email associated with user account
     */
    email:     string;
    icdCodes?: any[] | null;
    /**
     * where the user initiate BV lite from
     */
    initiateBvLiteSource?: InitiateBvLiteSource;
    /**
     * boolean field for whether the institution is identified as a top practice by SamaCare
     * sales
     */
    institutionIsTop: boolean;
    /**
     * name of the practice the user is associated with
     */
    institutionName: string;
    /**
     * describes the medical speciality of the practice
     */
    institutionSpecialty?: string;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId?:   string;
    insuranceGroupNumber?: string;
    insuranceMemberId?:    string;
    /**
     * name of patient insurance company
     */
    insuranceName?: string;
    /**
     * state in which the patient insurance plan is for
     */
    insuranceState?: string;
    /**
     * type of insurance plan
     */
    insuranceType?: InsuranceType;
    /**
     * boolean field for whether user role is admin or not
     */
    isAdmin: boolean;
    [property: string]: any;
}

/**
 * where the user initiate BV lite from
 */
export enum InitiateBvLiteSource {
    Bv = "BV",
    Pa = "PA",
    Treatment = "Treatment",
}

/**
 * type of insurance plan
 */
export enum InsuranceType {
    Commercial = "commercial",
    Medicaid = "medicaid",
    MedicareAdvantageOrReplacement = "medicare_advantage_or_replacement",
    Other = "other",
}

/**
 * An institution is created in SamaCare. The subject of these properties should be the
 * institution being created, not the user's institution who is performing the action.
 */
export interface InstitutionCreated {
    institutionId:         number;
    institutionIsTop?:     boolean;
    institutionName?:      string;
    institutionSpecialty?: string;
    [property: string]: any;
}

/**
 * An integration is set up for an institution in SamaCare. The subject of these properties
 * should be the institution for which the integration is being created, not the institution
 * of the user performing the action.
 */
export interface IntegrationCreated {
    ehrId?: string;
    /**
     * Examples: modMed, ecw, etc.
     */
    ehrSource?:            string;
    institutionId:         number;
    institutionIsTop?:     boolean;
    institutionName?:      string;
    institutionSpecialty?: string;
    integrationType?:      IntegrationType;
    [property: string]: any;
}

export enum IntegrationType {
    Api = "api",
    EhrBridge = "ehrBridge",
    Redox = "redox",
    Rpa = "rpa",
    Sftp = "sftp",
}

/**
 * Fires when a PA is deleted by a user
 */
export interface PaDeleted {
    /**
     * ID associated with user account
     */
    accountId:     number;
    deleteReason?: DeleteReason;
    /**
     * email associated with user account
     */
    email: string;
    /**
     * boolean field for whether the institution is identified as a top practice by SamaCare
     * sales
     */
    institutionIsTop: boolean;
    /**
     * name of the practice the user is associated with
     */
    institutionName: string;
    /**
     * describes the medical speciality of the practice
     */
    institutionSpecialty?: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    /**
     * boolean field for whether user role is admin or not
     */
    isAdmin: boolean;
    paType?: PaType;
    [property: string]: any;
}

export enum DeleteReason {
    Mistagged = "mistagged",
    Other = "other",
}

/**
 * SamaCare found zero or multiple matches to PA within clearinghouse.
 */
export interface PaExternalMatchFailed {
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    institutionId:         number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId: number;
    /**
     * name of patient insurance company
     */
    insuranceName: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    /**
     * Internal error type
     */
    internalError?: string;
    paStatus:       PaStatus;
    paType?:        PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    /**
     * Tracker Config ID
     */
    trackerConfigId?: number;
    /**
     * Tracker Config slug/key
     */
    trackerKey?: string;
    [property: string]: any;
}

export enum PaStatus {
    ActionRequired = "action_required",
    AppealPending = "appeal_pending",
    Approved = "approved",
    CaremetxFormNotFound = "caremetx_form_not_found",
    CaremetxFormReady = "caremetx_form_ready",
    Denied = "denied",
    Draft = "draft",
    Duplicate = "duplicate",
    EditAndResubmit = "edit_and_resubmit",
    FormsResearch = "forms_research",
    ModifiedApproval = "modified_approval",
    NoAuthorizationRequired = "no_authorization_required",
    Pending = "pending",
    PremiumRxSupport = "premium_rx_support",
    Sending = "sending",
    SendingFailed = "sending_failed",
    Sent = "sent",
    SentToHcp = "sent_to_hcp",
    Unrecognized = "unrecognized",
    Voided = "voided",
}

/**
 * SamaCare has identified PA's single, unique match within clearinghouse.
 */
export interface PaExternalMatched {
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    institutionId:         number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId: number;
    /**
     * name of patient insurance company
     */
    insuranceName: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    /**
     * Mode of successful unique match
     */
    mode?: string;
    /**
     * Count of clearinghouse results found
     */
    numSearchResults: number;
    /**
     * External identifier of PA
     */
    paExternalId: string;
    paStatus:     PaStatus;
    paType?:      PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    /**
     * Tracker Config ID
     */
    trackerConfigId?: number;
    /**
     * Tracker Config slug/key
     */
    trackerKey?: string;
    [property: string]: any;
}

/**
 * Clearinghouse has return potential result matches for PA.
 */
export interface PaExternalSearchCompleted {
    /**
     * Availity's Session ID
     */
    availitySessionId?: string;
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    institutionId:         number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId: number;
    /**
     * name of patient insurance company
     */
    insuranceName: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    /**
     * Count of clearinghouse results found
     */
    numSearchResults: number;
    paStatus:         PaStatus;
    paType?:          PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    /**
     * Tracker Config ID
     */
    trackerConfigId?: number;
    /**
     * Tracker Config slug/key
     */
    trackerKey?: string;
    [property: string]: any;
}

/**
 * SamaCare has successfully polled Availity's session URL for search results.
 */
export interface PaExternalSearchPolled {
    /**
     * Availity's Session ID
     */
    availitySessionId?: string;
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    institutionId:         number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId: number;
    /**
     * name of patient insurance company
     */
    insuranceName: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    paStatus:       PaStatus;
    paType?:        PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    /**
     * Tracker Config ID
     */
    trackerConfigId?: number;
    /**
     * Tracker Config slug/key
     */
    trackerKey?: string;
    [property: string]: any;
}

/**
 * SamaCare has successfully polled Availity's session URL for search results, but Availity
 * returned an error
 */
export interface PaExternalSearchProcessFailed {
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    /**
     * Set of errorneous fields
     */
    errorFields?: any[];
    /**
     * HTTP status code for error response
     */
    errorHttpCode?: number;
    /**
     * Validation errors
     */
    errorReasons?: any[];
    institutionId: number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId: number;
    /**
     * name of patient insurance company
     */
    insuranceName: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    /**
     * Internal error type
     */
    internalError?: string;
    paStatus:       PaStatus;
    paType?:        PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    /**
     * Keys of search parameters
     */
    searchParamsKeys?: any[];
    /**
     * Tracker Config ID
     */
    trackerConfigId?: number;
    /**
     * Tracker Config slug/key
     */
    trackerKey?: string;
    [property: string]: any;
}

/**
 * Clearinghouse has not successfully received SamaCare's search request submission.
 */
export interface PaExternalSearchRequestFailed {
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    /**
     * Set of errorneous fields
     */
    errorFields?: any[];
    /**
     * HTTP status code for error response
     */
    errorHttpCode?: number;
    /**
     * Validation errors
     */
    errorReasons?: any[];
    institutionId: number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId: number;
    /**
     * name of patient insurance company
     */
    insuranceName: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    /**
     * Internal error type
     */
    internalError: string;
    paStatus:      PaStatus;
    paType?:       PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    /**
     * Keys of search parameters
     */
    searchParamsKeys: any[];
    /**
     * Tracker Config ID
     */
    trackerConfigId?: number;
    /**
     * Tracker Config slug/key
     */
    trackerKey?: string;
    [property: string]: any;
}

/**
 * Clearinghouse has successfully received and acknowledge SamaCare's search request
 * submission.
 */
export interface PaExternalSearchRequestSucceeded {
    /**
     * Availity's Session ID
     */
    availitySessionId?: string;
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    institutionId:         number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId: number;
    /**
     * name of patient insurance company
     */
    insuranceName: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    paStatus:       PaStatus;
    paType?:        PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    /**
     * Keys of search parameters
     */
    searchParamsKeys: any[];
    /**
     * Tracker Config ID
     */
    trackerConfigId?: number;
    /**
     * Tracker Config slug/key
     */
    trackerKey?: string;
    [property: string]: any;
}

/**
 * SamaCare has attempted to submit a search request for potential matches to a PA.
 */
export interface PaExternalSearchRequested {
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    /**
     * Set of HCPCS codes.
     */
    hcpcsCodes?:   any[];
    institutionId: number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId: number;
    /**
     * name of patient insurance company
     */
    insuranceName: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    paStatus:       PaStatus;
    paType?:        PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    /**
     * Keys of Search Parameters
     */
    searchParamsKeys: any[];
    /**
     * Tracker Config ID
     */
    trackerConfigId?: number;
    /**
     * Tracker Config slug/key
     */
    trackerKey?: string;
    [property: string]: any;
}

export interface PaExternalSearchZeroMatches {
    /**
     * Availity's Session ID
     */
    availitySessionId?: string;
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    institutionId:         number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId?: number;
    /**
     * name of patient insurance company
     */
    insuranceName?: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    paStatus:       PaStatus;
    paType?:        PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?:      string;
    searchParamsKeys?: any[];
    /**
     * Tracker Config ID
     */
    trackerConfigId?: number;
    /**
     * Tracker Config slug/key
     */
    trackerKey?: string;
    [property: string]: any;
}

/**
 * PA's initial creation within SamaCare.
 */
export interface PaInitiated {
    institutionId:         number;
    institutionIsTop?:     boolean;
    institutionName?:      string;
    institutionSpecialty?: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    paType?:        PaType;
    /**
     * The ID of the portal
     */
    portalId?: number;
    /**
     * Portal's slug/key
     */
    portalKey?: string;
    /**
     * Portal's URL
     */
    portalOrigin?: string;
    /**
     * Status of PA
     */
    status: string;
    [property: string]: any;
}

/**
 * User views that list of all PAs.
 */
export interface PaListViewed {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    empty?: null;
    [property: string]: any;
}

/**
 * PA is now eligible for status checking.
 */
export interface PaStatusCheckEligible {
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    /**
     * Set of HCPCS codes.
     */
    hcpcsCodes?:   any[];
    institutionId: number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId?: number;
    /**
     * name of patient insurance company
     */
    insuranceName?: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    paStatus:       PaStatus;
    paType?:        PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    [property: string]: any;
}

/**
 * SamaCare successfully submitted & received a response for the 278I transaction.
 */
export interface PaStatusInquired {
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    institutionId:         number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId: number;
    /**
     * name of patient insurance company
     */
    insuranceName: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    paType?:        PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    /**
     * Tracker Config ID
     */
    trackerConfigId?: number;
    /**
     * Tracker Config slug/key
     */
    trackerKey?: string;
    [property: string]: any;
}

/**
 * SamaCare failed to submit a 278I transaction or receive a response.
 */
export interface PaStatusInquiryFailed {
    /**
     * Name of the clearing house.
     */
    clearingHouseName?: string;
    /**
     * Payer identifier for the clearing house.
     */
    clearingHousePayerId?: string;
    institutionId:         number;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId: number;
    /**
     * name of patient insurance company
     */
    insuranceName: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    paType?:        PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    /**
     * Tracker Config ID
     */
    trackerConfigId?: number;
    /**
     * Tracker Config slug/key
     */
    trackerKey?: string;
    [property: string]: any;
}

/**
 * A PA's status is updated
 */
export interface PaStatusUpdated {
    authorizationCorrespondenceCodes?: any[];
    institutionId:                     number;
    institutionIsTop?:                 boolean;
    institutionName?:                  string;
    institutionSpecialty?:             string;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId?:   string;
    insuranceGroupNumber?: string;
    insuranceMemberId?:    string;
    /**
     * name of patient insurance company
     */
    insuranceName?: string;
    /**
     * state in which the patient insurance plan is for
     */
    insuranceState?: string;
    /**
     * type of insurance plan
     */
    insuranceType?: InsuranceType;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    /**
     * the new, updated status of the authorization
     */
    newStatus: string;
    /**
     * the previous status of the authorization
     */
    oldStatus: string;
    paType?:   PaType;
    /**
     * Portal ID
     */
    portalId?: number;
    /**
     * Portal's slug/key
     */
    portalKey?: string;
    /**
     * Portal's URL
     */
    portalOrigin?: string;
    /**
     * Portal slug/key
     */
    portalTitle?: string;
    /**
     * the method in which the status was updated
     */
    updateSource?: UpdateSource;
    [property: string]: any;
}

/**
 * the method in which the status was updated
 */
export enum UpdateSource {
    EdiApi = "edi_api",
    EdiSftp = "edi_sftp",
    Extension = "extension",
    Rpa = "rpa",
    StatusChecker = "status_checker",
    WebApp = "web_app",
}

/**
 * User completes a step in the PA process
 */
export interface PaStepCompleted {
    /**
     * ID associated with user account
     */
    accountId: number;
    /**
     * email associated with user account
     */
    email: string;
    /**
     * boolean field for whether the institution is identified as a top practice by SamaCare
     * sales
     */
    institutionIsTop: boolean;
    /**
     * name of the practice the user is associated with
     */
    institutionName: string;
    /**
     * describes the medical speciality of the practice
     */
    institutionSpecialty?: string;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    /**
     * boolean field for whether user role is admin or not
     */
    isAdmin: boolean;
    /**
     * denotes which step in the PA process a user completed
     */
    paStep?: PaStep;
    paType?: PaType;
    [property: string]: any;
}

/**
 * denotes which step in the PA process a user completed
 */
export enum PaStep {
    CaremetxEnrollmentAndSubmit = "caremetx_enrollment_and_submit",
    EnrollmentAndSubmit = "enrollment_and_submit",
    FormDetails = "form_details",
    InitiatePriorAuthorization = "initiate_prior_authorization",
    LomnEdit = "lomn_edit",
    LomnFill = "lomn_fill",
    PatientAndInsurance = "patient_and_insurance",
    ProviderAndLocation = "provider_and_location",
    RequestDetails = "request_details",
    ReviewSignAndSubmit = "review_sign_and_submit",
    ServiceAndDiagnosis = "service_and_diagnosis",
}

/**
 * User views an individual PA's details.
 */
export interface PaViewed {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    empty?: null;
    [property: string]: any;
}

export interface PasswordResetRequested {
    /**
     * The email address requesting a password reset.
     */
    email: string;
    [property: string]: any;
}

/**
 * Fires when a user selects "Check Portal Status"
 */
export interface PortalStatusChecked {
    /**
     * ID associated with user account
     */
    accountId: number;
    /**
     * ID that may change from preliminary ID
     */
    authorizationCorrespondenceCodes?: any[];
    /**
     * email associated with user account
     */
    email: string;
    /**
     * code used to identify health services rendered
     */
    hcpcsCodes?: any[];
    /**
     * boolean field for whether the institution is identified as a top practice by SamaCare
     * sales
     */
    institutionIsTop: boolean;
    /**
     * name of the practice the user is associated with
     */
    institutionName: string;
    /**
     * describes the medical speciality of the practice
     */
    institutionSpecialty?: string;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId?:   string;
    insuranceGroupNumber?: string;
    insuranceMemberId?:    string;
    /**
     * name of patient insurance company
     */
    insuranceName?: string;
    /**
     * state in which the patient insurance plan is for
     */
    insuranceState?: string;
    /**
     * type of insurance plan
     */
    insuranceType?: InsuranceType;
    /**
     * ID of the PA
     */
    internalAuthId: number;
    /**
     * boolean field for whether user role is admin or not
     */
    isAdmin: boolean;
    paType?: PaType;
    /**
     * payer provided preliminary identifier
     */
    portalAuthorizationId?: string;
    [property: string]: any;
}

export interface UserLoggedOut {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    empty?: null;
    [property: string]: any;
}

/**
 * User selects "Login" after entering their credentials for SamaCare
 */
export interface UserSignedIn {
    /**
     * ID associated with user account
     */
    accountId: number;
    /**
     * email associated with user account
     */
    email: string;
    /**
     * boolean field for whether the institution is identified as a top practice by SamaCare
     * sales
     */
    institutionIsTop: boolean;
    /**
     * name of the practice the user is associated with
     */
    institutionName: string;
    /**
     * describes the medical speciality of the practice
     */
    institutionSpecialty?: string;
    /**
     * boolean field for whether user role is admin or not
     */
    isAdmin:             boolean;
    lastLoginTimestamp?: string;
    [property: string]: any;
}

/**
 * User view BV Lite details either from PA workflow or from BV grid
 */
export interface ViewBvLite {
    /**
     * ID associated with user account
     */
    accountId: number;
    /**
     * email associated with user account
     */
    email: string;
    /**
     * boolean field for whether the institution is identified as a top practice by SamaCare
     * sales
     */
    institutionIsTop: boolean;
    /**
     * name of the practice the user is associated with
     */
    institutionName: string;
    /**
     * describes the medical speciality of the practice
     */
    institutionSpecialty?: string;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId?:   string;
    insuranceGroupNumber?: string;
    insuranceMemberId?:    string;
    /**
     * name of patient insurance company
     */
    insuranceName?: string;
    /**
     * state in which the patient insurance plan is for
     */
    insuranceState?: string;
    /**
     * type of insurance plan
     */
    insuranceType?: InsuranceType;
    /**
     * boolean field for whether user role is admin or not
     */
    isAdmin: boolean;
    /**
     * where does the user view BV lite
     */
    viewSource: ViewSource;
    [property: string]: any;
}

/**
 * where does the user view BV lite
 */
export enum ViewSource {
    BvGrid = "bv_grid",
    Pa = "pa",
}

/**
 * User view full BV details either from PA workflow or from BV grid
 */
export interface ViewFullBv {
    /**
     * ID associated with user account
     */
    accountId: number;
    /**
     * email associated with user account
     */
    email: string;
    /**
     * boolean field for whether the institution is identified as a top practice by SamaCare
     * sales
     */
    institutionIsTop: boolean;
    /**
     * name of the practice the user is associated with
     */
    institutionName: string;
    /**
     * describes the medical speciality of the practice
     */
    institutionSpecialty?: string;
    /**
     * ID associated to the insurance company
     */
    insuranceCompanyId?:   string;
    insuranceGroupNumber?: string;
    insuranceMemberId?:    string;
    /**
     * name of patient insurance company
     */
    insuranceName?: string;
    /**
     * state in which the patient insurance plan is for
     */
    insuranceState?: string;
    /**
     * type of insurance plan
     */
    insuranceType?: InsuranceType;
    /**
     * boolean field for whether user role is admin or not
     */
    isAdmin: boolean;
    /**
     * where does the user view BV lite
     */
    viewSource?: ViewSource;
    [property: string]: any;
}

// Converts JSON strings to/from your types
// and asserts the results of JSON.parse at runtime
export class Convert {
    public static toAccountCreated(json: string): AccountCreated {
        return cast(JSON.parse(json), r("AccountCreated"));
    }

    public static accountCreatedToJson(value: AccountCreated): string {
        return JSON.stringify(uncast(value, r("AccountCreated")), null, 2);
    }

    public static toAccountDeactivated(json: string): AccountDeactivated {
        return cast(JSON.parse(json), r("AccountDeactivated"));
    }

    public static accountDeactivatedToJson(value: AccountDeactivated): string {
        return JSON.stringify(uncast(value, r("AccountDeactivated")), null, 2);
    }

    public static toAccountUnlocked(json: string): AccountUnlocked {
        return cast(JSON.parse(json), r("AccountUnlocked"));
    }

    public static accountUnlockedToJson(value: AccountUnlocked): string {
        return JSON.stringify(uncast(value, r("AccountUnlocked")), null, 2);
    }

    public static toExtensionAutofillAttempt(json: string): ExtensionAutofillAttempt {
        return cast(JSON.parse(json), r("ExtensionAutofillAttempt"));
    }

    public static extensionAutofillAttemptToJson(value: ExtensionAutofillAttempt): string {
        return JSON.stringify(uncast(value, r("ExtensionAutofillAttempt")), null, 2);
    }

    public static toExtensionAutofillFieldDetected(json: string): ExtensionAutofillFieldDetected {
        return cast(JSON.parse(json), r("ExtensionAutofillFieldDetected"));
    }

    public static extensionAutofillFieldDetectedToJson(value: ExtensionAutofillFieldDetected): string {
        return JSON.stringify(uncast(value, r("ExtensionAutofillFieldDetected")), null, 2);
    }

    public static toExtensionAutofillOverridden(json: string): ExtensionAutofillOverridden {
        return cast(JSON.parse(json), r("ExtensionAutofillOverridden"));
    }

    public static extensionAutofillOverriddenToJson(value: ExtensionAutofillOverridden): string {
        return JSON.stringify(uncast(value, r("ExtensionAutofillOverridden")), null, 2);
    }

    public static toExtensionFieldAutofilled(json: string): ExtensionFieldAutofilled {
        return cast(JSON.parse(json), r("ExtensionFieldAutofilled"));
    }

    public static extensionFieldAutofilledToJson(value: ExtensionFieldAutofilled): string {
        return JSON.stringify(uncast(value, r("ExtensionFieldAutofilled")), null, 2);
    }

    public static toExtensionFormViewed(json: string): ExtensionFormViewed {
        return cast(JSON.parse(json), r("ExtensionFormViewed"));
    }

    public static extensionFormViewedToJson(value: ExtensionFormViewed): string {
        return JSON.stringify(uncast(value, r("ExtensionFormViewed")), null, 2);
    }

    public static toExtensionInputFound(json: string): ExtensionInputFound {
        return cast(JSON.parse(json), r("ExtensionInputFound"));
    }

    public static extensionInputFoundToJson(value: ExtensionInputFound): string {
        return JSON.stringify(uncast(value, r("ExtensionInputFound")), null, 2);
    }

    public static toExtensionOpened(json: string): ExtensionOpened {
        return cast(JSON.parse(json), r("ExtensionOpened"));
    }

    public static extensionOpenedToJson(value: ExtensionOpened): string {
        return JSON.stringify(uncast(value, r("ExtensionOpened")), null, 2);
    }

    public static toExtensionSaveToSamaCareButtonClicked(json: string): ExtensionSaveToSamaCareButtonClicked {
        return cast(JSON.parse(json), r("ExtensionSaveToSamaCareButtonClicked"));
    }

    public static extensionSaveToSamaCareButtonClickedToJson(value: ExtensionSaveToSamaCareButtonClicked): string {
        return JSON.stringify(uncast(value, r("ExtensionSaveToSamaCareButtonClicked")), null, 2);
    }

    public static toExtensionViewAuthorizationButtonClicked(json: string): ExtensionViewAuthorizationButtonClicked {
        return cast(JSON.parse(json), r("ExtensionViewAuthorizationButtonClicked"));
    }

    public static extensionViewAuthorizationButtonClickedToJson(value: ExtensionViewAuthorizationButtonClicked): string {
        return JSON.stringify(uncast(value, r("ExtensionViewAuthorizationButtonClicked")), null, 2);
    }

    public static toInitiateBvLite(json: string): InitiateBvLite {
        return cast(JSON.parse(json), r("InitiateBvLite"));
    }

    public static initiateBvLiteToJson(value: InitiateBvLite): string {
        return JSON.stringify(uncast(value, r("InitiateBvLite")), null, 2);
    }

    public static toInstitutionCreated(json: string): InstitutionCreated {
        return cast(JSON.parse(json), r("InstitutionCreated"));
    }

    public static institutionCreatedToJson(value: InstitutionCreated): string {
        return JSON.stringify(uncast(value, r("InstitutionCreated")), null, 2);
    }

    public static toIntegrationCreated(json: string): IntegrationCreated {
        return cast(JSON.parse(json), r("IntegrationCreated"));
    }

    public static integrationCreatedToJson(value: IntegrationCreated): string {
        return JSON.stringify(uncast(value, r("IntegrationCreated")), null, 2);
    }

    public static toPaDeleted(json: string): PaDeleted {
        return cast(JSON.parse(json), r("PaDeleted"));
    }

    public static paDeletedToJson(value: PaDeleted): string {
        return JSON.stringify(uncast(value, r("PaDeleted")), null, 2);
    }

    public static toPaExternalMatchFailed(json: string): PaExternalMatchFailed {
        return cast(JSON.parse(json), r("PaExternalMatchFailed"));
    }

    public static paExternalMatchFailedToJson(value: PaExternalMatchFailed): string {
        return JSON.stringify(uncast(value, r("PaExternalMatchFailed")), null, 2);
    }

    public static toPaExternalMatched(json: string): PaExternalMatched {
        return cast(JSON.parse(json), r("PaExternalMatched"));
    }

    public static paExternalMatchedToJson(value: PaExternalMatched): string {
        return JSON.stringify(uncast(value, r("PaExternalMatched")), null, 2);
    }

    public static toPaExternalSearchCompleted(json: string): PaExternalSearchCompleted {
        return cast(JSON.parse(json), r("PaExternalSearchCompleted"));
    }

    public static paExternalSearchCompletedToJson(value: PaExternalSearchCompleted): string {
        return JSON.stringify(uncast(value, r("PaExternalSearchCompleted")), null, 2);
    }

    public static toPaExternalSearchPolled(json: string): PaExternalSearchPolled {
        return cast(JSON.parse(json), r("PaExternalSearchPolled"));
    }

    public static paExternalSearchPolledToJson(value: PaExternalSearchPolled): string {
        return JSON.stringify(uncast(value, r("PaExternalSearchPolled")), null, 2);
    }

    public static toPaExternalSearchProcessFailed(json: string): PaExternalSearchProcessFailed {
        return cast(JSON.parse(json), r("PaExternalSearchProcessFailed"));
    }

    public static paExternalSearchProcessFailedToJson(value: PaExternalSearchProcessFailed): string {
        return JSON.stringify(uncast(value, r("PaExternalSearchProcessFailed")), null, 2);
    }

    public static toPaExternalSearchRequestFailed(json: string): PaExternalSearchRequestFailed {
        return cast(JSON.parse(json), r("PaExternalSearchRequestFailed"));
    }

    public static paExternalSearchRequestFailedToJson(value: PaExternalSearchRequestFailed): string {
        return JSON.stringify(uncast(value, r("PaExternalSearchRequestFailed")), null, 2);
    }

    public static toPaExternalSearchRequestSucceeded(json: string): PaExternalSearchRequestSucceeded {
        return cast(JSON.parse(json), r("PaExternalSearchRequestSucceeded"));
    }

    public static paExternalSearchRequestSucceededToJson(value: PaExternalSearchRequestSucceeded): string {
        return JSON.stringify(uncast(value, r("PaExternalSearchRequestSucceeded")), null, 2);
    }

    public static toPaExternalSearchRequested(json: string): PaExternalSearchRequested {
        return cast(JSON.parse(json), r("PaExternalSearchRequested"));
    }

    public static paExternalSearchRequestedToJson(value: PaExternalSearchRequested): string {
        return JSON.stringify(uncast(value, r("PaExternalSearchRequested")), null, 2);
    }

    public static toPaExternalSearchZeroMatches(json: string): PaExternalSearchZeroMatches {
        return cast(JSON.parse(json), r("PaExternalSearchZeroMatches"));
    }

    public static paExternalSearchZeroMatchesToJson(value: PaExternalSearchZeroMatches): string {
        return JSON.stringify(uncast(value, r("PaExternalSearchZeroMatches")), null, 2);
    }

    public static toPaInitiated(json: string): PaInitiated {
        return cast(JSON.parse(json), r("PaInitiated"));
    }

    public static paInitiatedToJson(value: PaInitiated): string {
        return JSON.stringify(uncast(value, r("PaInitiated")), null, 2);
    }

    public static toPaListViewed(json: string): PaListViewed {
        return cast(JSON.parse(json), r("PaListViewed"));
    }

    public static paListViewedToJson(value: PaListViewed): string {
        return JSON.stringify(uncast(value, r("PaListViewed")), null, 2);
    }

    public static toPaStatusCheckEligible(json: string): PaStatusCheckEligible {
        return cast(JSON.parse(json), r("PaStatusCheckEligible"));
    }

    public static paStatusCheckEligibleToJson(value: PaStatusCheckEligible): string {
        return JSON.stringify(uncast(value, r("PaStatusCheckEligible")), null, 2);
    }

    public static toPaStatusInquired(json: string): PaStatusInquired {
        return cast(JSON.parse(json), r("PaStatusInquired"));
    }

    public static paStatusInquiredToJson(value: PaStatusInquired): string {
        return JSON.stringify(uncast(value, r("PaStatusInquired")), null, 2);
    }

    public static toPaStatusInquiryFailed(json: string): PaStatusInquiryFailed {
        return cast(JSON.parse(json), r("PaStatusInquiryFailed"));
    }

    public static paStatusInquiryFailedToJson(value: PaStatusInquiryFailed): string {
        return JSON.stringify(uncast(value, r("PaStatusInquiryFailed")), null, 2);
    }

    public static toPaStatusUpdated(json: string): PaStatusUpdated {
        return cast(JSON.parse(json), r("PaStatusUpdated"));
    }

    public static paStatusUpdatedToJson(value: PaStatusUpdated): string {
        return JSON.stringify(uncast(value, r("PaStatusUpdated")), null, 2);
    }

    public static toPaStepCompleted(json: string): PaStepCompleted {
        return cast(JSON.parse(json), r("PaStepCompleted"));
    }

    public static paStepCompletedToJson(value: PaStepCompleted): string {
        return JSON.stringify(uncast(value, r("PaStepCompleted")), null, 2);
    }

    public static toPaViewed(json: string): PaViewed {
        return cast(JSON.parse(json), r("PaViewed"));
    }

    public static paViewedToJson(value: PaViewed): string {
        return JSON.stringify(uncast(value, r("PaViewed")), null, 2);
    }

    public static toPasswordResetRequested(json: string): PasswordResetRequested {
        return cast(JSON.parse(json), r("PasswordResetRequested"));
    }

    public static passwordResetRequestedToJson(value: PasswordResetRequested): string {
        return JSON.stringify(uncast(value, r("PasswordResetRequested")), null, 2);
    }

    public static toPortalStatusChecked(json: string): PortalStatusChecked {
        return cast(JSON.parse(json), r("PortalStatusChecked"));
    }

    public static portalStatusCheckedToJson(value: PortalStatusChecked): string {
        return JSON.stringify(uncast(value, r("PortalStatusChecked")), null, 2);
    }

    public static toUserLoggedOut(json: string): UserLoggedOut {
        return cast(JSON.parse(json), r("UserLoggedOut"));
    }

    public static userLoggedOutToJson(value: UserLoggedOut): string {
        return JSON.stringify(uncast(value, r("UserLoggedOut")), null, 2);
    }

    public static toUserSignedIn(json: string): UserSignedIn {
        return cast(JSON.parse(json), r("UserSignedIn"));
    }

    public static userSignedInToJson(value: UserSignedIn): string {
        return JSON.stringify(uncast(value, r("UserSignedIn")), null, 2);
    }

    public static toViewBvLite(json: string): ViewBvLite {
        return cast(JSON.parse(json), r("ViewBvLite"));
    }

    public static viewBvLiteToJson(value: ViewBvLite): string {
        return JSON.stringify(uncast(value, r("ViewBvLite")), null, 2);
    }

    public static toViewFullBv(json: string): ViewFullBv {
        return cast(JSON.parse(json), r("ViewFullBv"));
    }

    public static viewFullBvToJson(value: ViewFullBv): string {
        return JSON.stringify(uncast(value, r("ViewFullBv")), null, 2);
    }
}

function invalidValue(typ: any, val: any, key: any = ''): never {
        if (key) {
                throw Error(`Invalid value for key "${key}". Expected type ${JSON.stringify(typ)} but got ${JSON.stringify(val)}`);
        }
        throw Error(`Invalid value ${JSON.stringify(val)} for type ${JSON.stringify(typ)}`, );
}

function jsonToJSProps(typ: any): any {
        if (typ.jsonToJS === undefined) {
                const map: any = {};
                typ.props.forEach((p: any) => map[p.json] = { key: p.js, typ: p.typ });
                typ.jsonToJS = map;
        }
        return typ.jsonToJS;
}

function jsToJSONProps(typ: any): any {
        if (typ.jsToJSON === undefined) {
                const map: any = {};
                typ.props.forEach((p: any) => map[p.js] = { key: p.json, typ: p.typ });
                typ.jsToJSON = map;
        }
        return typ.jsToJSON;
}

function transform(val: any, typ: any, getProps: any, key: any = ''): any {
        function transformPrimitive(typ: string, val: any): any {
                if (typeof typ === typeof val) return val;
                return invalidValue(typ, val, key);
        }

        function transformUnion(typs: any[], val: any): any {
                // val must validate against one typ in typs
                const l = typs.length;
                for (let i = 0; i < l; i++) {
                        const typ = typs[i];
                        try {
                                return transform(val, typ, getProps);
                        } catch (_) {}
                }
                return invalidValue(typs, val);
        }

        function transformEnum(cases: string[], val: any): any {
                if (cases.indexOf(val) !== -1) return val;
                return invalidValue(cases, val);
        }

        function transformArray(typ: any, val: any): any {
                // val must be an array with no invalid elements
                if (!Array.isArray(val)) return invalidValue("array", val);
                return val.map(el => transform(el, typ, getProps));
        }

        function transformDate(val: any): any {
                if (val === null) {
                        return null;
                }
                const d = new Date(val);
                if (isNaN(d.valueOf())) {
                        return invalidValue("Date", val);
                }
                return d;
        }

        function transformObject(props: { [k: string]: any }, additional: any, val: any): any {
                if (val === null || typeof val !== "object" || Array.isArray(val)) {
                        return invalidValue("object", val);
                }
                const result: any = {};
                Object.getOwnPropertyNames(props).forEach(key => {
                        const prop = props[key];
                        const v = Object.prototype.hasOwnProperty.call(val, key) ? val[key] : undefined;
                        result[prop.key] = transform(v, prop.typ, getProps, prop.key);
                });
                Object.getOwnPropertyNames(val).forEach(key => {
                        if (!Object.prototype.hasOwnProperty.call(props, key)) {
                                result[key] = transform(val[key], additional, getProps, key);
                        }
                });
                return result;
        }

        if (typ === "any") return val;
        if (typ === null) {
                if (val === null) return val;
                return invalidValue(typ, val);
        }
        if (typ === false) return invalidValue(typ, val);
        while (typeof typ === "object" && typ.ref !== undefined) {
                typ = typeMap[typ.ref];
        }
        if (Array.isArray(typ)) return transformEnum(typ, val);
        if (typeof typ === "object") {
                return typ.hasOwnProperty("unionMembers") ? transformUnion(typ.unionMembers, val)
                        : typ.hasOwnProperty("arrayItems")    ? transformArray(typ.arrayItems, val)
                        : typ.hasOwnProperty("props")         ? transformObject(getProps(typ), typ.additional, val)
                        : invalidValue(typ, val);
        }
        // Numbers can be parsed by Date but shouldn't be.
        if (typ === Date && typeof val !== "number") return transformDate(val);
        return transformPrimitive(typ, val);
}

function cast<T>(val: any, typ: any): T {
        return transform(val, typ, jsonToJSProps);
}

function uncast<T>(val: T, typ: any): any {
        return transform(val, typ, jsToJSONProps);
}

function a(typ: any) {
        return { arrayItems: typ };
}

function u(...typs: any[]) {
        return { unionMembers: typs };
}

function o(props: any[], additional: any) {
        return { props, additional };
}

function m(additional: any) {
        return { props: [], additional };
}

function r(name: string) {
        return { ref: name };
}

const typeMap: any = {
    "AccountCreated": o([
        { json: "accountEmail", js: "accountEmail", typ: "" },
        { json: "accountId", js: "accountId", typ: 3.14 },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionIsTop", js: "institutionIsTop", typ: u(undefined, true) },
        { json: "institutionName", js: "institutionName", typ: u(undefined, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "isAdmin", js: "isAdmin", typ: true },
        { json: "isReadOnly", js: "isReadOnly", typ: true },
        { json: "isSamaUser", js: "isSamaUser", typ: true },
    ], "any"),
    "AccountDeactivated": o([
        { json: "accountEmail", js: "accountEmail", typ: "" },
        { json: "accountId", js: "accountId", typ: 3.14 },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionIsTop", js: "institutionIsTop", typ: u(undefined, true) },
        { json: "institutionName", js: "institutionName", typ: u(undefined, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "isAdmin", js: "isAdmin", typ: true },
        { json: "isReadOnly", js: "isReadOnly", typ: true },
        { json: "isSamaUser", js: "isSamaUser", typ: true },
    ], "any"),
    "AccountUnlocked": o([
        { json: "accountEmail", js: "accountEmail", typ: "" },
        { json: "accountId", js: "accountId", typ: 3.14 },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionIsTop", js: "institutionIsTop", typ: u(undefined, true) },
        { json: "institutionName", js: "institutionName", typ: u(undefined, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "isAdmin", js: "isAdmin", typ: true },
        { json: "isReadOnly", js: "isReadOnly", typ: true },
        { json: "isSamaUser", js: "isSamaUser", typ: true },
    ], "any"),
    "ExtensionAutofillAttempt": o([
        { json: "fieldKey", js: "fieldKey", typ: "" },
        { json: "fieldXPath", js: "fieldXPath", typ: "" },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionName", js: "institutionName", typ: "" },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "pageKey", js: "pageKey", typ: u(undefined, "") },
        { json: "paType", js: "paType", typ: r("PaType") },
        { json: "portalId", js: "portalId", typ: 3.14 },
        { json: "portalTitle", js: "portalTitle", typ: "" },
        { json: "url", js: "url", typ: "" },
        { json: "version", js: "version", typ: "" },
    ], "any"),
    "ExtensionAutofillFieldDetected": o([
        { json: "autofillEnabled", js: "autofillEnabled", typ: u(undefined, true) },
        { json: "fieldKey", js: "fieldKey", typ: u(null, "") },
        { json: "fieldXPath", js: "fieldXPath", typ: u(null, "") },
        { json: "institutionId", js: "institutionId", typ: u(3.14, null) },
        { json: "institutionName", js: "institutionName", typ: u(null, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, u(null, "")) },
        { json: "internalAuthId", js: "internalAuthId", typ: u(3.14, null) },
        { json: "pageKey", js: "pageKey", typ: u(undefined, "") },
        { json: "paType", js: "paType", typ: r("PaType") },
        { json: "portalId", js: "portalId", typ: 3.14 },
        { json: "portalTitle", js: "portalTitle", typ: "" },
        { json: "url", js: "url", typ: "" },
        { json: "version", js: "version", typ: "" },
    ], "any"),
    "ExtensionAutofillOverridden": o([
        { json: "fieldKey", js: "fieldKey", typ: u(null, "") },
        { json: "fieldValueNew", js: "fieldValueNew", typ: u(undefined, "") },
        { json: "fieldValueOld", js: "fieldValueOld", typ: "" },
        { json: "fieldXPath", js: "fieldXPath", typ: u(null, "") },
        { json: "institutionId", js: "institutionId", typ: u(3.14, null) },
        { json: "institutionName", js: "institutionName", typ: u(null, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, u(null, "")) },
        { json: "internalAuthId", js: "internalAuthId", typ: u(3.14, null) },
        { json: "pageKey", js: "pageKey", typ: u(undefined, "") },
        { json: "paType", js: "paType", typ: r("PaType") },
        { json: "portalId", js: "portalId", typ: 3.14 },
        { json: "portalTitle", js: "portalTitle", typ: "" },
        { json: "url", js: "url", typ: "" },
        { json: "version", js: "version", typ: "" },
    ], "any"),
    "ExtensionFieldAutofilled": o([
        { json: "errorKind", js: "errorKind", typ: u(r("ErrorKind"), null) },
        { json: "fieldKey", js: "fieldKey", typ: "" },
        { json: "fieldValue", js: "fieldValue", typ: u(null, "") },
        { json: "fieldXPath", js: "fieldXPath", typ: "" },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionName", js: "institutionName", typ: "" },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "pageKey", js: "pageKey", typ: u(undefined, "") },
        { json: "paType", js: "paType", typ: r("PaType") },
        { json: "portalId", js: "portalId", typ: 3.14 },
        { json: "portalTitle", js: "portalTitle", typ: "" },
        { json: "success", js: "success", typ: true },
        { json: "url", js: "url", typ: "" },
        { json: "version", js: "version", typ: "" },
    ], "any"),
    "ExtensionFormViewed": o([
        { json: "fieldHcpcsValue", js: "fieldHcpcsValue", typ: u(undefined, "") },
        { json: "fieldIsNewValue", js: "fieldIsNewValue", typ: u(undefined, true) },
        { json: "fieldPatientIdValue", js: "fieldPatientIdValue", typ: u(undefined, "") },
        { json: "fieldPayorValue", js: "fieldPayorValue", typ: u(undefined, "") },
        { json: "fieldPortalAuthReferenceId", js: "fieldPortalAuthReferenceId", typ: u(undefined, "") },
        { json: "fieldStatusValue", js: "fieldStatusValue", typ: u(undefined, "") },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionIsTop", js: "institutionIsTop", typ: u(undefined, true) },
        { json: "institutionName", js: "institutionName", typ: u(undefined, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "openSource", js: "openSource", typ: u(undefined, u(r("OpenSource"), null)) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalKey", js: "portalKey", typ: u(undefined, "") },
        { json: "portalOrigin", js: "portalOrigin", typ: u(undefined, "") },
        { json: "version", js: "version", typ: u(undefined, "") },
    ], "any"),
    "ExtensionInputFound": o([
        { json: "fieldInputId", js: "fieldInputId", typ: u(undefined, "") },
        { json: "fieldInputName", js: "fieldInputName", typ: u(undefined, "") },
        { json: "fieldXPath", js: "fieldXPath", typ: "" },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionName", js: "institutionName", typ: "" },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 0 },
        { json: "paType", js: "paType", typ: r("PaType") },
        { json: "portalId", js: "portalId", typ: 3.14 },
        { json: "portalTitle", js: "portalTitle", typ: "" },
        { json: "url", js: "url", typ: "" },
        { json: "version", js: "version", typ: "" },
    ], "any"),
    "ExtensionOpened": o([
        { json: "institutionId", js: "institutionId", typ: u(undefined, 3.14) },
        { json: "institutionIsTop", js: "institutionIsTop", typ: u(undefined, true) },
        { json: "institutionName", js: "institutionName", typ: u(undefined, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "openSource", js: "openSource", typ: u(r("OpenSource"), null) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalKey", js: "portalKey", typ: u(undefined, "") },
        { json: "portalOrigin", js: "portalOrigin", typ: u(undefined, "") },
        { json: "version", js: "version", typ: u(undefined, "") },
    ], "any"),
    "ExtensionSaveToSamaCareButtonClicked": o([
        { json: "fieldHcpcsValue", js: "fieldHcpcsValue", typ: u(undefined, "") },
        { json: "fieldIsNewValue", js: "fieldIsNewValue", typ: u(undefined, true) },
        { json: "fieldPatientIdValue", js: "fieldPatientIdValue", typ: u(undefined, "") },
        { json: "fieldPayorValue", js: "fieldPayorValue", typ: u(undefined, "") },
        { json: "fieldPortalAuthReferenceId", js: "fieldPortalAuthReferenceId", typ: u(undefined, "") },
        { json: "fieldStatusValue", js: "fieldStatusValue", typ: u(undefined, "") },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionIsTop", js: "institutionIsTop", typ: u(undefined, true) },
        { json: "institutionName", js: "institutionName", typ: u(undefined, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalKey", js: "portalKey", typ: u(undefined, "") },
        { json: "portalOrigin", js: "portalOrigin", typ: u(undefined, "") },
        { json: "version", js: "version", typ: u(undefined, "") },
    ], "any"),
    "ExtensionViewAuthorizationButtonClicked": o([
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionIsTop", js: "institutionIsTop", typ: u(undefined, true) },
        { json: "institutionName", js: "institutionName", typ: u(undefined, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalKey", js: "portalKey", typ: u(undefined, "") },
        { json: "portalOrigin", js: "portalOrigin", typ: u(undefined, "") },
        { json: "version", js: "version", typ: u(undefined, "") },
    ], "any"),
    "InitiateBvLite": o([
        { json: "accountId", js: "accountId", typ: 3.14 },
        { json: "DrugOptionId", js: "drugOptionId", typ: u(undefined, u(3.14, null)) },
        { json: "email", js: "email", typ: "" },
        { json: "icdCodes", js: "icdCodes", typ: u(undefined, u(a("any"), null)) },
        { json: "initiateBvLiteSource", js: "initiateBvLiteSource", typ: u(undefined, r("InitiateBvLiteSource")) },
        { json: "institutionIsTop", js: "institutionIsTop", typ: true },
        { json: "institutionName", js: "institutionName", typ: "" },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: u(undefined, "") },
        { json: "insuranceGroupNumber", js: "insuranceGroupNumber", typ: u(undefined, "") },
        { json: "insuranceMemberId", js: "insuranceMemberId", typ: u(undefined, "") },
        { json: "insuranceName", js: "insuranceName", typ: u(undefined, "") },
        { json: "insuranceState", js: "insuranceState", typ: u(undefined, "") },
        { json: "insuranceType", js: "insuranceType", typ: u(undefined, r("InsuranceType")) },
        { json: "isAdmin", js: "isAdmin", typ: true },
    ], "any"),
    "InstitutionCreated": o([
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionIsTop", js: "institutionIsTop", typ: u(undefined, true) },
        { json: "institutionName", js: "institutionName", typ: u(undefined, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
    ], "any"),
    "IntegrationCreated": o([
        { json: "ehrId", js: "ehrId", typ: u(undefined, "") },
        { json: "ehrSource", js: "ehrSource", typ: u(undefined, "") },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionIsTop", js: "institutionIsTop", typ: u(undefined, true) },
        { json: "institutionName", js: "institutionName", typ: u(undefined, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "integrationType", js: "integrationType", typ: u(undefined, r("IntegrationType")) },
    ], "any"),
    "PaDeleted": o([
        { json: "accountId", js: "accountId", typ: 3.14 },
        { json: "deleteReason", js: "deleteReason", typ: u(undefined, r("DeleteReason")) },
        { json: "email", js: "email", typ: "" },
        { json: "institutionIsTop", js: "institutionIsTop", typ: true },
        { json: "institutionName", js: "institutionName", typ: "" },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "isAdmin", js: "isAdmin", typ: true },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
    ], "any"),
    "PaExternalMatchFailed": o([
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: 3.14 },
        { json: "insuranceName", js: "insuranceName", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "internalError", js: "internalError", typ: u(undefined, "") },
        { json: "paStatus", js: "paStatus", typ: r("PaStatus") },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "trackerConfigId", js: "trackerConfigId", typ: u(undefined, 3.14) },
        { json: "trackerKey", js: "trackerKey", typ: u(undefined, "") },
    ], "any"),
    "PaExternalMatched": o([
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: 3.14 },
        { json: "insuranceName", js: "insuranceName", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "mode", js: "mode", typ: u(undefined, "") },
        { json: "numSearchResults", js: "numSearchResults", typ: 3.14 },
        { json: "paExternalId", js: "paExternalId", typ: "" },
        { json: "paStatus", js: "paStatus", typ: r("PaStatus") },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "trackerConfigId", js: "trackerConfigId", typ: u(undefined, 3.14) },
        { json: "trackerKey", js: "trackerKey", typ: u(undefined, "") },
    ], "any"),
    "PaExternalSearchCompleted": o([
        { json: "availitySessionID", js: "availitySessionId", typ: u(undefined, "") },
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: 3.14 },
        { json: "insuranceName", js: "insuranceName", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "numSearchResults", js: "numSearchResults", typ: 3.14 },
        { json: "paStatus", js: "paStatus", typ: r("PaStatus") },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "trackerConfigId", js: "trackerConfigId", typ: u(undefined, 3.14) },
        { json: "trackerKey", js: "trackerKey", typ: u(undefined, "") },
    ], "any"),
    "PaExternalSearchPolled": o([
        { json: "availitySessionID", js: "availitySessionId", typ: u(undefined, "") },
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: 3.14 },
        { json: "insuranceName", js: "insuranceName", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "paStatus", js: "paStatus", typ: r("PaStatus") },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "trackerConfigId", js: "trackerConfigId", typ: u(undefined, 3.14) },
        { json: "trackerKey", js: "trackerKey", typ: u(undefined, "") },
    ], "any"),
    "PaExternalSearchProcessFailed": o([
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "errorFields", js: "errorFields", typ: u(undefined, a("any")) },
        { json: "errorHttpCode", js: "errorHttpCode", typ: u(undefined, 3.14) },
        { json: "errorReasons", js: "errorReasons", typ: u(undefined, a("any")) },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: 3.14 },
        { json: "insuranceName", js: "insuranceName", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "internalError", js: "internalError", typ: u(undefined, "") },
        { json: "paStatus", js: "paStatus", typ: r("PaStatus") },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "searchParamsKeys", js: "searchParamsKeys", typ: u(undefined, a("any")) },
        { json: "trackerConfigId", js: "trackerConfigId", typ: u(undefined, 3.14) },
        { json: "trackerKey", js: "trackerKey", typ: u(undefined, "") },
    ], "any"),
    "PaExternalSearchRequestFailed": o([
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "errorFields", js: "errorFields", typ: u(undefined, a("any")) },
        { json: "errorHttpCode", js: "errorHttpCode", typ: u(undefined, 3.14) },
        { json: "errorReasons", js: "errorReasons", typ: u(undefined, a("any")) },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: 3.14 },
        { json: "insuranceName", js: "insuranceName", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "internalError", js: "internalError", typ: "" },
        { json: "paStatus", js: "paStatus", typ: r("PaStatus") },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "searchParamsKeys", js: "searchParamsKeys", typ: a("any") },
        { json: "trackerConfigId", js: "trackerConfigId", typ: u(undefined, 3.14) },
        { json: "trackerKey", js: "trackerKey", typ: u(undefined, "") },
    ], "any"),
    "PaExternalSearchRequestSucceeded": o([
        { json: "availitySessionID", js: "availitySessionId", typ: u(undefined, "") },
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: 3.14 },
        { json: "insuranceName", js: "insuranceName", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "paStatus", js: "paStatus", typ: r("PaStatus") },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "searchParamsKeys", js: "searchParamsKeys", typ: a("any") },
        { json: "trackerConfigId", js: "trackerConfigId", typ: u(undefined, 3.14) },
        { json: "trackerKey", js: "trackerKey", typ: u(undefined, "") },
    ], "any"),
    "PaExternalSearchRequested": o([
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "hcpcsCodes", js: "hcpcsCodes", typ: u(undefined, a("any")) },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: 3.14 },
        { json: "insuranceName", js: "insuranceName", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "paStatus", js: "paStatus", typ: r("PaStatus") },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "searchParamsKeys", js: "searchParamsKeys", typ: a("any") },
        { json: "trackerConfigId", js: "trackerConfigId", typ: u(undefined, 3.14) },
        { json: "trackerKey", js: "trackerKey", typ: u(undefined, "") },
    ], "any"),
    "PaExternalSearchZeroMatches": o([
        { json: "availitySessionID", js: "availitySessionId", typ: u(undefined, "") },
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: u(undefined, 3.14) },
        { json: "insuranceName", js: "insuranceName", typ: u(undefined, "") },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "paStatus", js: "paStatus", typ: r("PaStatus") },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "searchParamsKeys", js: "searchParamsKeys", typ: u(undefined, a("any")) },
        { json: "trackerConfigId", js: "trackerConfigId", typ: u(undefined, 3.14) },
        { json: "trackerKey", js: "trackerKey", typ: u(undefined, "") },
    ], "any"),
    "PaInitiated": o([
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionIsTop", js: "institutionIsTop", typ: u(undefined, true) },
        { json: "institutionName", js: "institutionName", typ: u(undefined, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalKey", js: "portalKey", typ: u(undefined, "") },
        { json: "portalOrigin", js: "portalOrigin", typ: u(undefined, "") },
        { json: "status", js: "status", typ: "" },
    ], "any"),
    "PaListViewed": o([
        { json: "_", js: "empty", typ: u(undefined, null) },
    ], "any"),
    "PaStatusCheckEligible": o([
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "hcpcsCodes", js: "hcpcsCodes", typ: u(undefined, a("any")) },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: u(undefined, 3.14) },
        { json: "insuranceName", js: "insuranceName", typ: u(undefined, "") },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "paStatus", js: "paStatus", typ: r("PaStatus") },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
    ], "any"),
    "PaStatusInquired": o([
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: 3.14 },
        { json: "insuranceName", js: "insuranceName", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "trackerConfigId", js: "trackerConfigId", typ: u(undefined, 3.14) },
        { json: "trackerKey", js: "trackerKey", typ: u(undefined, "") },
    ], "any"),
    "PaStatusInquiryFailed": o([
        { json: "clearingHouseName", js: "clearingHouseName", typ: u(undefined, "") },
        { json: "clearingHousePayerId", js: "clearingHousePayerId", typ: u(undefined, "") },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: 3.14 },
        { json: "insuranceName", js: "insuranceName", typ: "" },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "trackerConfigId", js: "trackerConfigId", typ: u(undefined, 3.14) },
        { json: "trackerKey", js: "trackerKey", typ: u(undefined, "") },
    ], "any"),
    "PaStatusUpdated": o([
        { json: "authorizationCorrespondenceCodes", js: "authorizationCorrespondenceCodes", typ: u(undefined, a("any")) },
        { json: "institutionId", js: "institutionId", typ: 3.14 },
        { json: "institutionIsTop", js: "institutionIsTop", typ: u(undefined, true) },
        { json: "institutionName", js: "institutionName", typ: u(undefined, "") },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: u(undefined, "") },
        { json: "insuranceGroupNumber", js: "insuranceGroupNumber", typ: u(undefined, "") },
        { json: "insuranceMemberId", js: "insuranceMemberId", typ: u(undefined, "") },
        { json: "insuranceName", js: "insuranceName", typ: u(undefined, "") },
        { json: "insuranceState", js: "insuranceState", typ: u(undefined, "") },
        { json: "insuranceType", js: "insuranceType", typ: u(undefined, r("InsuranceType")) },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "newStatus", js: "newStatus", typ: "" },
        { json: "oldStatus", js: "oldStatus", typ: "" },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalId", js: "portalId", typ: u(undefined, 3.14) },
        { json: "portalKey", js: "portalKey", typ: u(undefined, "") },
        { json: "portalOrigin", js: "portalOrigin", typ: u(undefined, "") },
        { json: "portalTitle", js: "portalTitle", typ: u(undefined, "") },
        { json: "updateSource", js: "updateSource", typ: u(undefined, r("UpdateSource")) },
    ], "any"),
    "PaStepCompleted": o([
        { json: "accountId", js: "accountId", typ: 3.14 },
        { json: "email", js: "email", typ: "" },
        { json: "institutionIsTop", js: "institutionIsTop", typ: true },
        { json: "institutionName", js: "institutionName", typ: "" },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "isAdmin", js: "isAdmin", typ: true },
        { json: "paStep", js: "paStep", typ: u(undefined, r("PaStep")) },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
    ], "any"),
    "PaViewed": o([
        { json: "_", js: "empty", typ: u(undefined, null) },
    ], "any"),
    "PasswordResetRequested": o([
        { json: "email", js: "email", typ: "" },
    ], "any"),
    "PortalStatusChecked": o([
        { json: "accountId", js: "accountId", typ: 3.14 },
        { json: "authorizationCorrespondenceCodes", js: "authorizationCorrespondenceCodes", typ: u(undefined, a("any")) },
        { json: "email", js: "email", typ: "" },
        { json: "hcpcsCodes", js: "hcpcsCodes", typ: u(undefined, a("any")) },
        { json: "institutionIsTop", js: "institutionIsTop", typ: true },
        { json: "institutionName", js: "institutionName", typ: "" },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: u(undefined, "") },
        { json: "insuranceGroupNumber", js: "insuranceGroupNumber", typ: u(undefined, "") },
        { json: "insuranceMemberId", js: "insuranceMemberId", typ: u(undefined, "") },
        { json: "insuranceName", js: "insuranceName", typ: u(undefined, "") },
        { json: "insuranceState", js: "insuranceState", typ: u(undefined, "") },
        { json: "insuranceType", js: "insuranceType", typ: u(undefined, r("InsuranceType")) },
        { json: "internalAuthId", js: "internalAuthId", typ: 3.14 },
        { json: "isAdmin", js: "isAdmin", typ: true },
        { json: "paType", js: "paType", typ: u(undefined, r("PaType")) },
        { json: "portalAuthorizationId", js: "portalAuthorizationId", typ: u(undefined, "") },
    ], "any"),
    "UserLoggedOut": o([
        { json: "_", js: "empty", typ: u(undefined, null) },
    ], "any"),
    "UserSignedIn": o([
        { json: "accountId", js: "accountId", typ: 3.14 },
        { json: "email", js: "email", typ: "" },
        { json: "institutionIsTop", js: "institutionIsTop", typ: true },
        { json: "institutionName", js: "institutionName", typ: "" },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "isAdmin", js: "isAdmin", typ: true },
        { json: "lastLoginTimestamp", js: "lastLoginTimestamp", typ: u(undefined, "") },
    ], "any"),
    "ViewBvLite": o([
        { json: "accountId", js: "accountId", typ: 3.14 },
        { json: "email", js: "email", typ: "" },
        { json: "institutionIsTop", js: "institutionIsTop", typ: true },
        { json: "institutionName", js: "institutionName", typ: "" },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: u(undefined, "") },
        { json: "insuranceGroupNumber", js: "insuranceGroupNumber", typ: u(undefined, "") },
        { json: "insuranceMemberId", js: "insuranceMemberId", typ: u(undefined, "") },
        { json: "insuranceName", js: "insuranceName", typ: u(undefined, "") },
        { json: "insuranceState", js: "insuranceState", typ: u(undefined, "") },
        { json: "insuranceType", js: "insuranceType", typ: u(undefined, r("InsuranceType")) },
        { json: "isAdmin", js: "isAdmin", typ: true },
        { json: "viewSource", js: "viewSource", typ: r("ViewSource") },
    ], "any"),
    "ViewFullBv": o([
        { json: "accountId", js: "accountId", typ: 3.14 },
        { json: "email", js: "email", typ: "" },
        { json: "institutionIsTop", js: "institutionIsTop", typ: true },
        { json: "institutionName", js: "institutionName", typ: "" },
        { json: "institutionSpecialty", js: "institutionSpecialty", typ: u(undefined, "") },
        { json: "insuranceCompanyId", js: "insuranceCompanyId", typ: u(undefined, "") },
        { json: "insuranceGroupNumber", js: "insuranceGroupNumber", typ: u(undefined, "") },
        { json: "insuranceMemberId", js: "insuranceMemberId", typ: u(undefined, "") },
        { json: "insuranceName", js: "insuranceName", typ: u(undefined, "") },
        { json: "insuranceState", js: "insuranceState", typ: u(undefined, "") },
        { json: "insuranceType", js: "insuranceType", typ: u(undefined, r("InsuranceType")) },
        { json: "isAdmin", js: "isAdmin", typ: true },
        { json: "viewSource", js: "viewSource", typ: u(undefined, r("ViewSource")) },
    ], "any"),
    "PaType": [
        "edi",
        "enrollment",
        "external",
        "form",
        "multi_party",
        "portal",
        "portal_external",
        "referral",
    ],
    "ErrorKind": [
        "NO_VALUE",
        "UNKNOWN",
    ],
    "OpenSource": [
        "autodetection",
        "navbar_icon",
        "widget",
    ],
    "InitiateBvLiteSource": [
        "BV",
        "PA",
        "Treatment",
    ],
    "InsuranceType": [
        "commercial",
        "medicaid",
        "medicare_advantage_or_replacement",
        "other",
    ],
    "IntegrationType": [
        "api",
        "ehrBridge",
        "redox",
        "rpa",
        "sftp",
    ],
    "DeleteReason": [
        "mistagged",
        "other",
    ],
    "PaStatus": [
        "action_required",
        "appeal_pending",
        "approved",
        "caremetx_form_not_found",
        "caremetx_form_ready",
        "denied",
        "draft",
        "duplicate",
        "edit_and_resubmit",
        "forms_research",
        "modified_approval",
        "no_authorization_required",
        "pending",
        "premium_rx_support",
        "sending",
        "sending_failed",
        "sent",
        "sent_to_hcp",
        "unrecognized",
        "voided",
    ],
    "UpdateSource": [
        "edi_api",
        "edi_sftp",
        "extension",
        "rpa",
        "status_checker",
        "web_app",
    ],
    "PaStep": [
        "caremetx_enrollment_and_submit",
        "enrollment_and_submit",
        "form_details",
        "initiate_prior_authorization",
        "lomn_edit",
        "lomn_fill",
        "patient_and_insurance",
        "provider_and_location",
        "request_details",
        "review_sign_and_submit",
        "service_and_diagnosis",
    ],
    "ViewSource": [
        "bv_grid",
        "pa",
    ],
};

/**
 * Ajv is a peer dependency for development builds. It's used to apply run-time validation
 * to message payloads before passing them on to the underlying analytics instance.
 *
 * Note that the production bundle does not depend on Ajv.
 * 
 * You can install it with: `npm install --save-dev ajv`.
 */
import Ajv, { ErrorObject } from 'ajv'

/**
 * The analytics.js snippet should be available via window.analytics.
 * You can install it by following instructions at: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/
 * Make sure to also include the TypeScript declarations with: `npm install @segment/analytics-next` (install with --save-dev for type definitions only).
 *
 * If you don't want to use the snippet, you can also install the `@segment/analytics-next` library as a *production* dependency and use it like this: 
 * ```ts
 * import { AnalyticsBrowser } from '@segment/analytics-next' 
 * import { setTypewriterOptions } from './analytics'
 *
 * const analytics = AnalyticsBrowser.load({ writeKey: 'SEGMENT_WRITE_KEY' })
 * 
 * setTypewriterOptions({ analytics: analytics })
 */
import type { AnalyticsSnippet, Analytics, AnalyticsBrowser, Options } from '@segment/analytics-next'

declare global {
    interface Window {
        analytics: AnalyticsSnippet;
    }
}

type AnyAnalytics = AnalyticsSnippet | Analytics | AnalyticsBrowser

/** The callback exposed by analytics.js. */
export type Callback = () => void

export type ViolationHandler = (
    message: Record<string, any>,
    violations: ErrorObject[]
) => void

/**
 * The default handler that is fired if none is supplied with setTypewriterOptions.
 * This handler will log a warning message to the console.
 */
export const defaultValidationErrorHandler: ViolationHandler = (message, violations) => {
    const msg = JSON.stringify(
        {
            type: 'Typewriter JSON Schema Validation Error',
            description:
                `You made an analytics call (${message.event}) using Typewriter that doesn't match the ` +
                'Tracking Plan spec.',
            errors: violations,
        },
        undefined,
        2,
    );

    console.warn(msg);
};

let onViolation = defaultValidationErrorHandler

let analytics: () => AnyAnalytics | undefined = () => {
    return window.analytics;
};

/** Options to customize the runtime behavior of a Typewriter client. */
export interface TypewriterOptions {
    /**
     * Underlying analytics instance where analytics calls are forwarded on to.
     * Defaults to window.analytics.
     */
    analytics?: AnyAnalytics;
    /**
     * Handler fired when if an event does not match its spec. This handler
     * does not fire in production mode, because it requires inlining the full
     * JSON Schema spec for each event in your Tracking Plan.
     *
     * By default, it will throw errors if NODE_ENV = "test" so that tests will fail
     * if a message does not match the spec. Otherwise, errors will be logged to stderr.
     */
    onViolation?: ViolationHandler;
}

/**
 * Updates the run-time configuration of this Typewriter client.
 *
 * @param {TypewriterOptions} options - the options to upsert
 *
 * @typedef {Object} TypewriterOptions
 * @property {AnyAnalytics} [analytics] - Underlying analytics instance where analytics
 * 		calls are forwarded on to. Defaults to window.analytics.
 * @property {Function} [onViolation] - Handler fired when if an event does not match its spec. This handler does not fire in
 * 		production mode, because it requires inlining the full JSON Schema spec for each event in your Tracking Plan. By default,
 * 		it will throw errors if NODE_ENV="test" so that tests will fail if a message does not match the spec. Otherwise, errors
 * 		will be logged to stderr.
 */
export function setTypewriterOptions(options: TypewriterOptions) {
    analytics = options.analytics ? () => options.analytics || window.analytics : analytics;
    onViolation = options.onViolation || onViolation
}

/**
    * Validates a message against a JSON Schema using Ajv. If the message
    * is invalid, the `onViolation` handler will be called.
    */
function validateAgainstSchema(
    message: Record<string, any>,
    schema: object
) {
    const ajv = new Ajv({ allErrors: true, verbose: true })

    if (!ajv.validate(schema, message) && ajv.errors) {
        onViolation(message, ajv.errors)
    }
}

/**
 * Helper to attach metadata on Typewriter to outbound requests.
 * This is used for attribution and debugging by the Segment team.
 */
function withTypewriterContext(message: Options = {}): Options {
    return {
        ...message,
        context: {
            ...(message.context || {}),
            typewriter: {
                language: 'typescript',
                version: '9.2.0',
            },
        },
    };
}

/**
 * Fires a 'AccountCreated' track call.
 *
 * @param AccountCreated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function accountCreated(props: AccountCreated, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Account_Created","$schema":"http://json-schema.org/draft-07/schema#","description":"SamaCare account is created. The subject of these properties should be the account being created, not the account performing the action.","properties":{"accountEmail":{"$id":"/properties/accountEmail","description":"Specifies the email address of the account that is the target of the action, distinct from the account initiating the action.","pattern":"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$\n","type":"string"},"accountId":{"$id":"/properties/accountId","description":"specifies the ID of the account that is the target of the action, distinct from the account initiating the action.","type":"number"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"isAdmin":{"$id":"/properties/isAdmin","description":"","type":"boolean"},"isReadOnly":{"$id":"/properties/isReadOnly","description":"","type":"boolean"},"isSamaUser":{"$id":"/properties/isSamaUser","description":"","type":"boolean"}},"required":["institutionId","isAdmin","isReadOnly","isSamaUser","accountEmail","accountId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Account Created', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'AccountDeactivated' track call.
 *
 * @param AccountDeactivated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function accountDeactivated(props: AccountDeactivated, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Account_Deactivated","$schema":"http://json-schema.org/draft-07/schema#","description":"SamaCare account is deactivated. The subject of these properties should be the account being deactivated, not the account performing the action.","properties":{"accountEmail":{"$id":"/properties/accountEmail","description":"Specifies the email address of the account that is the target of the action, distinct from the account initiating the action.","pattern":"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$\n","type":"string"},"accountId":{"$id":"/properties/accountId","description":"specifies the ID of the account that is the target of the action, distinct from the account initiating the action.","type":"number"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"isAdmin":{"$id":"/properties/isAdmin","description":"","type":"boolean"},"isReadOnly":{"$id":"/properties/isReadOnly","description":"","type":"boolean"},"isSamaUser":{"$id":"/properties/isSamaUser","description":"","type":"boolean"}},"required":["isSamaUser","accountEmail","institutionId","accountId","isAdmin","isReadOnly"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Account Deactivated', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'AccountUnlocked' track call.
 *
 * @param AccountUnlocked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function accountUnlocked(props: AccountUnlocked, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Account_Unlocked","$schema":"http://json-schema.org/draft-07/schema#","description":"SamaCare account is unlocked. The subject of these properties should be the account being unlocked, not the account performing the action.","properties":{"accountEmail":{"$id":"/properties/accountEmail","description":"Specifies the email address of the account that is the target of the action, distinct from the account initiating the action.","pattern":"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$\n","type":"string"},"accountId":{"$id":"/properties/accountId","description":"specifies the ID of the account that is the target of the action, distinct from the account initiating the action.","type":"number"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"isAdmin":{"$id":"/properties/isAdmin","description":"","type":"boolean"},"isReadOnly":{"$id":"/properties/isReadOnly","description":"","type":"boolean"},"isSamaUser":{"$id":"/properties/isSamaUser","description":"","type":"boolean"}},"required":["accountEmail","accountId","isAdmin","isReadOnly","isSamaUser","institutionId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Account Unlocked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExtensionAutofillAttempt' track call.
 *
 * @param ExtensionAutofillAttempt props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function extensionAutofillAttempt(props: ExtensionAutofillAttempt, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Extension_Autofill_Attempt","$schema":"http://json-schema.org/draft-07/schema#","description":"Attempt an autofill on extension","properties":{"fieldKey":{"$id":"/properties/fieldKey","description":"Extension input field key","type":"string"},"fieldXPath":{"$id":"/properties/fieldXPath","description":"Input field xpath","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"pageKey":{"$id":"/properties/pageKey","description":"Internal defined page key","type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"url":{"$id":"/properties/url","description":"Url of the current page","type":"string"},"version":{"$id":"/properties/version","description":"User’s current version of extension","type":"string"}},"required":["fieldKey","fieldXPath","institutionId","institutionName","institutionSpecialty","internalAuthId","paType","portalId","portalTitle","url","version"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Extension Autofill Attempt', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExtensionAutofillFieldDetected' track call.
 *
 * @param ExtensionAutofillFieldDetected props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function extensionAutofillFieldDetected(props: ExtensionAutofillFieldDetected, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Extension_Autofill_Field_Detected","$schema":"http://json-schema.org/draft-07/schema#","description":"Successfully detected autofill field on web extension","properties":{"autofillEnabled":{"$id":"/properties/autofillEnabled","description":"Whether display autofill icon","type":"boolean"},"fieldKey":{"$id":"/properties/fieldKey","description":"Extension input field key","type":["string","null"]},"fieldXPath":{"$id":"/properties/fieldXPath","description":"Input field xpath","type":["string","null"]},"institutionId":{"$id":"/properties/institutionId","description":"","type":["number","null"]},"institutionName":{"$id":"/properties/institutionName","description":"","type":["string","null"]},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":["string","null"]},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":["number","null"]},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"pageKey":{"$id":"/properties/pageKey","description":"Internal defined page key","type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"url":{"$id":"/properties/url","description":"Url of the current page that ","type":"string"},"version":{"$id":"/properties/version","description":"User’s current version of extension","type":"string"}},"required":["internalAuthId","institutionId","fieldKey","portalId","paType","institutionName","version","portalTitle","url","fieldXPath"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Extension Autofill Field Detected', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExtensionAutofillOverridden' track call.
 *
 * @param ExtensionAutofillOverridden props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function extensionAutofillOverridden(props: ExtensionAutofillOverridden, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Extension_Autofill_Overridden","$schema":"http://json-schema.org/draft-07/schema#","description":"User trying to override the value extension autofilled","properties":{"fieldKey":{"$id":"/properties/fieldKey","description":"Extension input field key","type":["string","null"]},"fieldValueNew":{"$id":"/properties/fieldValueNew","description":"The value user override","type":"string"},"fieldValueOld":{"$id":"/properties/fieldValueOld","description":"The value extension trying to autofill","type":"string"},"fieldXPath":{"$id":"/properties/fieldXPath","description":"Input field xpath","type":["string","null"]},"institutionId":{"$id":"/properties/institutionId","description":"","type":["number","null"]},"institutionName":{"$id":"/properties/institutionName","description":"","type":["string","null"]},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":["string","null"]},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":["number","null"]},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"pageKey":{"$id":"/properties/pageKey","description":"Internal defined page key","type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"url":{"$id":"/properties/url","description":"Url of the current page that ","type":"string"},"version":{"$id":"/properties/version","description":"User’s current version of extension","type":"string"}},"required":["fieldKey","fieldValueOld","fieldXPath","institutionId","institutionName","internalAuthId","paType","portalId","portalTitle","url","version"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Extension Autofill Overridden', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExtensionFieldAutofilled' track call.
 *
 * @param ExtensionFieldAutofilled props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function extensionFieldAutofilled(props: ExtensionFieldAutofilled, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Extension_Field_Autofilled","$schema":"http://json-schema.org/draft-07/schema#","description":"Autofill value stored in PA to extension input field","properties":{"errorKind":{"$id":"/properties/errorKind","description":"","enum":["NO_VALUE","UNKNOWN",null],"type":["string","null"]},"fieldKey":{"$id":"/properties/fieldKey","description":"Extension input field key","type":"string"},"fieldValue":{"$id":"/properties/fieldValue","description":"Input field value from authorization config","type":["string","null"]},"fieldXPath":{"$id":"/properties/fieldXPath","description":"Input field xpath","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"pageKey":{"$id":"/properties/pageKey","description":"Internal defined page key","type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"success":{"$id":"/properties/success","description":"whether the autofill is successfully filled","type":"boolean"},"url":{"$id":"/properties/url","description":"","type":"string"},"version":{"$id":"/properties/version","description":"User’s current version of extension","type":"string"}},"required":["errorKind","fieldKey","fieldValue","fieldXPath","institutionId","institutionName","institutionSpecialty","internalAuthId","paType","portalId","portalTitle","success","url","version"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Extension Field Autofilled', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExtensionFormViewed' track call.
 *
 * @param ExtensionFormViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function extensionFormViewed(props: ExtensionFormViewed, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Extension_Form_Viewed","$schema":"http://json-schema.org/draft-07/schema#","description":"User views Extension (sidebar) form.","properties":{"fieldHcpcsValue":{"$id":"/properties/fieldHcpcsValue","description":"HCPCS code of value from Extension's HCPCS field","type":"string"},"fieldIsNewValue":{"$id":"/properties/fieldIsNewValue","description":"True if Extension's New PA radio field is selected","type":"boolean"},"fieldPatientIdValue":{"$id":"/properties/fieldPatientIdValue","description":"Patient ID of value from Extension's Patient field","type":"string"},"fieldPayorValue":{"$id":"/properties/fieldPayorValue","description":"Value from Extension's Payor field","type":"string"},"fieldPortalAuthReferenceId":{"$id":"/properties/fieldPortalAuthReferenceId","description":"Value from Extension's Portal Reference ID field","type":"string"},"fieldStatusValue":{"$id":"/properties/fieldStatusValue","description":"Value from Extension's Status field","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"openSource":{"$id":"/properties/openSource","description":"","enum":["autodetection","navbar_icon","widget",null],"type":["string","null"]},"portalId":{"$id":"/properties/portalId","description":"The ID of the portal","type":"number"},"portalKey":{"$id":"/properties/portalKey","description":"Portal's slug/key","type":"string"},"portalOrigin":{"$id":"/properties/portalOrigin","description":"Portal's URL","type":"string"},"version":{"$id":"/properties/version","description":"User’s current version of extension","type":"string"}},"required":["institutionId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Extension Form Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExtensionInputFound' track call.
 *
 * @param ExtensionInputFound props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function extensionInputFound(props: ExtensionInputFound, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Extension_Input_Found","$schema":"http://json-schema.org/draft-07/schema#","description":"Extension detect a new input on screen","properties":{"fieldInputId":{"$id":"/properties/fieldInputId","description":"ID of the found field if there is one","type":"string"},"fieldInputName":{"$id":"/properties/fieldInputName","description":"name of the found field if there is one","type":"string"},"fieldXPath":{"$id":"/properties/fieldXPath","description":"XPath of the found field","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"","type":"integer"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"url":{"$id":"/properties/url","description":"URL of the page the input is found on","type":"string"},"version":{"$id":"/properties/version","description":"","type":"string"}},"required":["institutionId","institutionName","institutionSpecialty","internalAuthId","paType","portalId","portalTitle","version","url","fieldXPath"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Extension Input Found', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExtensionOpened' track call.
 *
 * @param ExtensionOpened props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function extensionOpened(props: ExtensionOpened, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Extension_Opened","$schema":"http://json-schema.org/draft-07/schema#","description":"Extension is launched in user's browser.","properties":{"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"openSource":{"$id":"/properties/openSource","description":"Source of how extension was opened","enum":["autodetection","navbar_icon",null,"widget"],"type":["string","null"]},"portalId":{"$id":"/properties/portalId","description":"The ID of the portal ","type":"number"},"portalKey":{"$id":"/properties/portalKey","description":"Portal's slug/key","type":"string"},"portalOrigin":{"$id":"/properties/portalOrigin","description":"Portal's URL","type":"string"},"version":{"$id":"/properties/version","description":"User’s current version of extension","type":"string"}},"required":["openSource"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Extension Opened', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExtensionSaveToSamaCareButtonClicked' track call.
 *
 * @param ExtensionSaveToSamaCareButtonClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function extensionSaveToSamaCareButtonClicked(props: ExtensionSaveToSamaCareButtonClicked, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Extension_Save_to_SamaCare_Button_Clicked","$schema":"http://json-schema.org/draft-07/schema#","description":"User performs CTA to save PA to SamaCare","properties":{"fieldHcpcsValue":{"$id":"/properties/fieldHcpcsValue","description":"HCPCS code of value from Extension's HCPCS field","type":"string"},"fieldIsNewValue":{"$id":"/properties/fieldIsNewValue","description":"True if Extension's New PA radio field is selected","type":"boolean"},"fieldPatientIdValue":{"$id":"/properties/fieldPatientIdValue","description":"Patient ID of value from Extension's Patient field","type":"string"},"fieldPayorValue":{"$id":"/properties/fieldPayorValue","description":"Value from Extension's Payor field","type":"string"},"fieldPortalAuthReferenceId":{"$id":"/properties/fieldPortalAuthReferenceId","description":"Value from Extension's Portal Reference ID field","type":"string"},"fieldStatusValue":{"$id":"/properties/fieldStatusValue","description":"Value from Extension's Status field","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"portalId":{"$id":"/properties/portalId","description":"The ID of the portal","type":"number"},"portalKey":{"$id":"/properties/portalKey","description":"Portal's slug/key","type":"string"},"portalOrigin":{"$id":"/properties/portalOrigin","description":"Portal's URL","type":"string"},"version":{"$id":"/properties/version","description":"User’s current version of extension","type":"string"}},"required":["institutionId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Extension Save to SamaCare Button Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ExtensionViewAuthorizationButtonClicked' track call.
 *
 * @param ExtensionViewAuthorizationButtonClicked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function extensionViewAuthorizationButtonClicked(props: ExtensionViewAuthorizationButtonClicked, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Extension_View_Authorization_Button_Clicked","$schema":"http://json-schema.org/draft-07/schema#","description":"User performs CTA to redirect to web app to view PA.","properties":{"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"portalId":{"$id":"/properties/portalId","description":"The ID of the portal","type":"number"},"portalKey":{"$id":"/properties/portalKey","description":"Portal's slug/key","type":"string"},"portalOrigin":{"$id":"/properties/portalOrigin","description":"Portal's URL","type":"string"},"version":{"$id":"/properties/version","description":"User’s current version of extension","type":"string"}},"required":["institutionId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Extension View Authorization Button Clicked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'InitiateBvLite' track call.
 *
 * @param InitiateBvLite props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function initiateBvLite(props: InitiateBvLite, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Initiate_BV_lite","$schema":"http://json-schema.org/draft-07/schema#","description":"Create BV lite from unified BV check","properties":{"DrugOptionId":{"$id":"/properties/DrugOptionId","description":"","type":["number","null"]},"accountId":{"$id":"/properties/accountId","description":"ID associated with user account","type":"number"},"email":{"$id":"/properties/email","description":"email associated with user account","type":"string"},"icdCodes":{"$id":"/properties/icdCodes","description":"","type":["array","null"]},"initiateBvLiteSource":{"$id":"/properties/initiateBvLiteSource","description":"where the user initiate BV lite from","enum":["BV","PA","Treatment"],"type":"string"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"boolean field for whether the institution is identified as a top practice by SamaCare sales","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"name of the practice the user is associated with","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"describes the medical speciality of the practice ","type":"string"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"string"},"insuranceGroupNumber":{"$id":"/properties/insuranceGroupNumber","description":"","type":"string"},"insuranceMemberId":{"$id":"/properties/insuranceMemberId","description":"","type":"string"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"insuranceState":{"$id":"/properties/insuranceState","description":"state in which the patient insurance plan is for","type":"string"},"insuranceType":{"$id":"/properties/insuranceType","description":"type of insurance plan","enum":["commercial","medicaid","medicare_advantage_or_replacement","other"],"type":"string"},"isAdmin":{"$id":"/properties/isAdmin","description":"boolean field for whether user role is admin or not","type":"boolean"}},"required":["accountId","email","institutionIsTop","institutionName","isAdmin"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Initiate BV lite', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'InstitutionCreated' track call.
 *
 * @param InstitutionCreated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function institutionCreated(props: InstitutionCreated, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Institution_Created","$schema":"http://json-schema.org/draft-07/schema#","description":"An institution is created in SamaCare. The subject of these properties should be the institution being created, not the user's institution who is performing the action.","properties":{"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"}},"required":["institutionId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Institution Created', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'IntegrationCreated' track call.
 *
 * @param IntegrationCreated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function integrationCreated(props: IntegrationCreated, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Integration_Created","$schema":"http://json-schema.org/draft-07/schema#","description":"An integration is set up for an institution in SamaCare. The subject of these properties should be the institution for which the integration is being created, not the institution of the user performing the action.","properties":{"ehrId":{"$id":"/properties/ehrId","description":"","type":"string"},"ehrSource":{"$id":"/properties/ehrSource","description":"Examples: modMed, ecw, etc.","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"integrationType":{"$id":"/properties/integrationType","description":"","enum":["api","ehrBridge","redox","rpa","sftp"],"type":"string"}},"required":["institutionId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Integration Created', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaDeleted' track call.
 *
 * @param PaDeleted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paDeleted(props: PaDeleted, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_Deleted","$schema":"http://json-schema.org/draft-07/schema#","description":"Fires when a PA is deleted by a user","properties":{"accountId":{"$id":"/properties/accountId","description":"ID associated with user account","type":"number"},"deleteReason":{"$id":"/properties/deleteReason","description":"","enum":["mistagged","other"],"type":"string"},"email":{"$id":"/properties/email","description":"email associated with user account","type":"string"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"boolean field for whether the institution is identified as a top practice by SamaCare sales","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"name of the practice the user is associated with","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"describes the medical speciality of the practice ","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"isAdmin":{"$id":"/properties/isAdmin","description":"boolean field for whether user role is admin or not","type":"boolean"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"}},"required":["isAdmin","accountId","email","institutionIsTop","institutionName","internalAuthId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA Deleted', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaExternalMatchFailed' track call.
 *
 * @param PaExternalMatchFailed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paExternalMatchFailed(props: PaExternalMatchFailed, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_External_Match_Failed","$schema":"http://json-schema.org/draft-07/schema#","description":"SamaCare found zero or multiple matches to PA within clearinghouse.","properties":{"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"internalError":{"$id":"/properties/internalError","description":"Internal error type","type":"string"},"paStatus":{"$id":"/properties/paStatus","description":"","enum":["action_required","appeal_pending","approved","caremetx_form_not_found","caremetx_form_ready","denied","draft","duplicate","edit_and_resubmit","forms_research","modified_approval","no_authorization_required","pending","premium_rx_support","sending","sending_failed","sent","sent_to_hcp","unrecognized","voided"],"type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"trackerConfigId":{"$id":"/properties/trackerConfigId","description":"Tracker Config ID","type":"number"},"trackerKey":{"$id":"/properties/trackerKey","description":"Tracker Config slug/key","type":"string"}},"required":["internalAuthId","paStatus","institutionId","insuranceCompanyId","insuranceName"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA External Match Failed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaExternalMatched' track call.
 *
 * @param PaExternalMatched props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paExternalMatched(props: PaExternalMatched, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_External_Matched","$schema":"http://json-schema.org/draft-07/schema#","description":"SamaCare has identified PA's single, unique match within clearinghouse.","properties":{"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"mode":{"$id":"/properties/mode","description":"Mode of successful unique match","type":"string"},"numSearchResults":{"$id":"/properties/numSearchResults","description":"Count of clearinghouse results found","type":"number"},"paExternalId":{"$id":"/properties/paExternalId","description":"External identifier of PA","type":"string"},"paStatus":{"$id":"/properties/paStatus","description":"","enum":["action_required","appeal_pending","approved","caremetx_form_not_found","caremetx_form_ready","denied","draft","duplicate","edit_and_resubmit","forms_research","modified_approval","no_authorization_required","pending","premium_rx_support","sending","sending_failed","sent","sent_to_hcp","unrecognized","voided"],"type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"trackerConfigId":{"$id":"/properties/trackerConfigId","description":"Tracker Config ID","type":"number"},"trackerKey":{"$id":"/properties/trackerKey","description":"Tracker Config slug/key","type":"string"}},"required":["internalAuthId","paExternalId","institutionId","insuranceCompanyId","numSearchResults","paStatus","insuranceName"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA External Matched', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaExternalSearchCompleted' track call.
 *
 * @param PaExternalSearchCompleted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paExternalSearchCompleted(props: PaExternalSearchCompleted, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_External_Search_Completed","$schema":"http://json-schema.org/draft-07/schema#","description":"Clearinghouse has return potential result matches for PA.","properties":{"availitySessionID":{"$id":"/properties/availitySessionID","description":"Availity's Session ID","type":"string"},"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"numSearchResults":{"$id":"/properties/numSearchResults","description":"Count of clearinghouse results found","type":"number"},"paStatus":{"$id":"/properties/paStatus","description":"","enum":["action_required","appeal_pending","approved","caremetx_form_not_found","caremetx_form_ready","denied","draft","duplicate","edit_and_resubmit","forms_research","modified_approval","no_authorization_required","pending","premium_rx_support","sending","sending_failed","sent","sent_to_hcp","unrecognized","voided"],"type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"trackerConfigId":{"$id":"/properties/trackerConfigId","description":"Tracker Config ID","type":"number"},"trackerKey":{"$id":"/properties/trackerKey","description":"Tracker Config slug/key","type":"string"}},"required":["numSearchResults","insuranceName","paStatus","institutionId","insuranceCompanyId","internalAuthId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA External Search Completed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaExternalSearchPolled' track call.
 *
 * @param PaExternalSearchPolled props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paExternalSearchPolled(props: PaExternalSearchPolled, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_External_Search_Polled","$schema":"http://json-schema.org/draft-07/schema#","description":"SamaCare has successfully polled Availity's session URL for search results.","properties":{"availitySessionID":{"$id":"/properties/availitySessionID","description":"Availity's Session ID","type":"string"},"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"paStatus":{"$id":"/properties/paStatus","description":"","enum":["action_required","appeal_pending","approved","caremetx_form_not_found","caremetx_form_ready","denied","draft","duplicate","edit_and_resubmit","forms_research","modified_approval","no_authorization_required","pending","premium_rx_support","sending","sending_failed","sent","sent_to_hcp","unrecognized","voided"],"type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"trackerConfigId":{"$id":"/properties/trackerConfigId","description":"Tracker Config ID","type":"number"},"trackerKey":{"$id":"/properties/trackerKey","description":"Tracker Config slug/key","type":"string"}},"required":["institutionId","insuranceCompanyId","paStatus","insuranceName","internalAuthId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA External Search Polled', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaExternalSearchProcessFailed' track call.
 *
 * @param PaExternalSearchProcessFailed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paExternalSearchProcessFailed(props: PaExternalSearchProcessFailed, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_External_Search_Process_Failed","$schema":"http://json-schema.org/draft-07/schema#","description":"SamaCare has successfully polled Availity's session URL for search results, but Availity returned an error","properties":{"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"errorFields":{"$id":"/properties/errorFields","description":"Set of errorneous fields","type":"array"},"errorHttpCode":{"$id":"/properties/errorHttpCode","description":"HTTP status code for error response","type":"number"},"errorReasons":{"$id":"/properties/errorReasons","description":"Validation errors","type":"array"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"internalError":{"$id":"/properties/internalError","description":"Internal error type","type":"string"},"paStatus":{"$id":"/properties/paStatus","description":"","enum":["action_required","appeal_pending","approved","caremetx_form_not_found","caremetx_form_ready","denied","draft","duplicate","edit_and_resubmit","forms_research","modified_approval","no_authorization_required","pending","premium_rx_support","sending","sending_failed","sent","sent_to_hcp","unrecognized","voided"],"type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"searchParamsKeys":{"$id":"/properties/searchParamsKeys","description":"Keys of search parameters","type":"array"},"trackerConfigId":{"$id":"/properties/trackerConfigId","description":"Tracker Config ID","type":"number"},"trackerKey":{"$id":"/properties/trackerKey","description":"Tracker Config slug/key","type":"string"}},"required":["insuranceCompanyId","insuranceName","internalAuthId","paStatus","institutionId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA External Search Process Failed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaExternalSearchRequestFailed' track call.
 *
 * @param PaExternalSearchRequestFailed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paExternalSearchRequestFailed(props: PaExternalSearchRequestFailed, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_External_Search_Request_Failed","$schema":"http://json-schema.org/draft-07/schema#","description":"Clearinghouse has not successfully received SamaCare's search request submission.","properties":{"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"errorFields":{"$id":"/properties/errorFields","description":"Set of errorneous fields","type":"array"},"errorHttpCode":{"$id":"/properties/errorHttpCode","description":"HTTP status code for error response","type":"number"},"errorReasons":{"$id":"/properties/errorReasons","description":"Validation errors","type":"array"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"internalError":{"$id":"/properties/internalError","description":"Internal error type","type":"string"},"paStatus":{"$id":"/properties/paStatus","description":"","enum":["action_required","appeal_pending","approved","caremetx_form_not_found","caremetx_form_ready","denied","draft","duplicate","edit_and_resubmit","forms_research","modified_approval","no_authorization_required","pending","premium_rx_support","sending","sending_failed","sent","sent_to_hcp","unrecognized","voided"],"type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"searchParamsKeys":{"$id":"/properties/searchParamsKeys","description":"Keys of search parameters","type":"array"},"trackerConfigId":{"$id":"/properties/trackerConfigId","description":"Tracker Config ID","type":"number"},"trackerKey":{"$id":"/properties/trackerKey","description":"Tracker Config slug/key","type":"string"}},"required":["insuranceCompanyId","internalAuthId","internalError","paStatus","institutionId","insuranceName","searchParamsKeys"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA External Search Request Failed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaExternalSearchRequestSucceeded' track call.
 *
 * @param PaExternalSearchRequestSucceeded props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paExternalSearchRequestSucceeded(props: PaExternalSearchRequestSucceeded, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_External_Search_Request_Succeeded","$schema":"http://json-schema.org/draft-07/schema#","description":"Clearinghouse has successfully received and acknowledge SamaCare's search request submission.","properties":{"availitySessionID":{"$id":"/properties/availitySessionID","description":"Availity's Session ID","type":"string"},"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"paStatus":{"$id":"/properties/paStatus","description":"","enum":["action_required","appeal_pending","approved","caremetx_form_not_found","caremetx_form_ready","denied","draft","duplicate","edit_and_resubmit","forms_research","modified_approval","no_authorization_required","pending","premium_rx_support","sending","sending_failed","sent","sent_to_hcp","unrecognized","voided"],"type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"searchParamsKeys":{"$id":"/properties/searchParamsKeys","description":"Keys of search parameters","type":"array"},"trackerConfigId":{"$id":"/properties/trackerConfigId","description":"Tracker Config ID","type":"number"},"trackerKey":{"$id":"/properties/trackerKey","description":"Tracker Config slug/key","type":"string"}},"required":["searchParamsKeys","institutionId","insuranceCompanyId","insuranceName","internalAuthId","paStatus"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA External Search Request Succeeded', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaExternalSearchRequested' track call.
 *
 * @param PaExternalSearchRequested props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paExternalSearchRequested(props: PaExternalSearchRequested, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_External_Search_Requested","$schema":"http://json-schema.org/draft-07/schema#","description":"SamaCare has attempted to submit a search request for potential matches to a PA.","properties":{"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"hcpcsCodes":{"$id":"/properties/hcpcsCodes","description":"Set of HCPCS codes.","type":"array"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"paStatus":{"$id":"/properties/paStatus","description":"","enum":["action_required","appeal_pending","approved","caremetx_form_not_found","caremetx_form_ready","denied","draft","duplicate","edit_and_resubmit","forms_research","modified_approval","no_authorization_required","pending","premium_rx_support","sending","sending_failed","sent","sent_to_hcp","unrecognized","voided"],"type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"searchParamsKeys":{"$id":"/properties/searchParamsKeys","description":"Keys of Search Parameters","type":"array"},"trackerConfigId":{"$id":"/properties/trackerConfigId","description":"Tracker Config ID","type":"number"},"trackerKey":{"$id":"/properties/trackerKey","description":"Tracker Config slug/key","type":"string"}},"required":["insuranceCompanyId","insuranceName","internalAuthId","paStatus","searchParamsKeys","institutionId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA External Search Requested', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaExternalSearchZeroMatches' track call.
 *
 * @param PaExternalSearchZeroMatches props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paExternalSearchZeroMatches(props: PaExternalSearchZeroMatches, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_External_Search_Zero_Matches","$schema":"http://json-schema.org/draft-07/schema#","properties":{"availitySessionID":{"$id":"/properties/availitySessionID","description":"Availity's Session ID","type":"string"},"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"paStatus":{"$id":"/properties/paStatus","description":"","enum":["action_required","appeal_pending","approved","caremetx_form_not_found","caremetx_form_ready","denied","draft","duplicate","edit_and_resubmit","forms_research","modified_approval","no_authorization_required","pending","premium_rx_support","sending","sending_failed","sent","sent_to_hcp","unrecognized","voided"],"type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"searchParamsKeys":{"$id":"/properties/searchParamsKeys","description":"","type":"array"},"trackerConfigId":{"$id":"/properties/trackerConfigId","description":"Tracker Config ID","type":"number"},"trackerKey":{"$id":"/properties/trackerKey","description":"Tracker Config slug/key","type":"string"}},"required":["institutionId","internalAuthId","paStatus"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA External Search Zero Matches', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaInitiated' track call.
 *
 * @param PaInitiated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paInitiated(props: PaInitiated, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_Initiated","$schema":"http://json-schema.org/draft-07/schema#","description":"PA's initial creation within SamaCare.","properties":{"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"The ID of the portal","type":"number"},"portalKey":{"$id":"/properties/portalKey","description":"Portal's slug/key","type":"string"},"portalOrigin":{"$id":"/properties/portalOrigin","description":"Portal's URL","type":"string"},"status":{"$id":"/properties/status","description":"Status of PA","type":"string"}},"required":["institutionId","internalAuthId","status"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA Initiated', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaListViewed' track call.
 *
 * @param PaListViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paListViewed(props: PaListViewed, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_List_Viewed","$schema":"http://json-schema.org/draft-07/schema#","description":"User views that list of all PAs.","properties":{"_":{"type":"null","$id":"/properties/_","description":"This property is added as a workaround to generate type for events without properties"}},"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA List Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaStatusCheckEligible' track call.
 *
 * @param PaStatusCheckEligible props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paStatusCheckEligible(props: PaStatusCheckEligible, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_Status_Check_Eligible","$schema":"http://json-schema.org/draft-07/schema#","description":"PA is now eligible for status checking.","properties":{"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"hcpcsCodes":{"$id":"/properties/hcpcsCodes","description":"Set of HCPCS codes.","type":"array"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"paStatus":{"$id":"/properties/paStatus","description":"","enum":["action_required","appeal_pending","approved","caremetx_form_not_found","caremetx_form_ready","denied","draft","duplicate","edit_and_resubmit","forms_research","modified_approval","no_authorization_required","pending","premium_rx_support","sending","sending_failed","sent","sent_to_hcp","unrecognized","voided"],"type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"}},"required":["institutionId","paStatus","internalAuthId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA Status Check Eligible', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaStatusInquired' track call.
 *
 * @param PaStatusInquired props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paStatusInquired(props: PaStatusInquired, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_Status_Inquired","$schema":"http://json-schema.org/draft-07/schema#","description":"SamaCare successfully submitted & received a response for the 278I transaction.","properties":{"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"trackerConfigId":{"$id":"/properties/trackerConfigId","description":"Tracker Config ID","type":"number"},"trackerKey":{"$id":"/properties/trackerKey","description":"Tracker Config slug/key","type":"string"}},"required":["internalAuthId","institutionId","insuranceCompanyId","insuranceName"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA Status Inquired', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaStatusInquiryFailed' track call.
 *
 * @param PaStatusInquiryFailed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paStatusInquiryFailed(props: PaStatusInquiryFailed, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_Status_Inquiry_Failed","$schema":"http://json-schema.org/draft-07/schema#","description":"SamaCare failed to submit a 278I transaction or receive a response.","properties":{"clearingHouseName":{"$id":"/properties/clearingHouseName","description":"Name of the clearing house.","type":"string"},"clearingHousePayerId":{"$id":"/properties/clearingHousePayerId","description":"Payer identifier for the clearing house.","type":"string"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"number"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"trackerConfigId":{"$id":"/properties/trackerConfigId","description":"Tracker Config ID","type":"number"},"trackerKey":{"$id":"/properties/trackerKey","description":"Tracker Config slug/key","type":"string"}},"required":["institutionId","insuranceCompanyId","insuranceName","internalAuthId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA Status Inquiry Failed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaStatusUpdated' track call.
 *
 * @param PaStatusUpdated props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paStatusUpdated(props: PaStatusUpdated, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_Status_Updated","$schema":"http://json-schema.org/draft-07/schema#","description":"A PA's status is updated","properties":{"authorizationCorrespondenceCodes":{"$id":"/properties/authorizationCorrespondenceCodes","description":"","type":"array"},"institutionId":{"$id":"/properties/institutionId","description":"","type":"number"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"","type":"string"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"string"},"insuranceGroupNumber":{"$id":"/properties/insuranceGroupNumber","description":"","type":"string"},"insuranceMemberId":{"$id":"/properties/insuranceMemberId","description":"","type":"string"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"insuranceState":{"$id":"/properties/insuranceState","description":"state in which the patient insurance plan is for","type":"string"},"insuranceType":{"$id":"/properties/insuranceType","description":"type of insurance plan","enum":["commercial","medicaid","medicare_advantage_or_replacement","other"],"type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"newStatus":{"$id":"/properties/newStatus","description":"the new, updated status of the authorization","type":"string"},"oldStatus":{"$id":"/properties/oldStatus","description":"the previous status of the authorization","type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalId":{"$id":"/properties/portalId","description":"Portal ID","type":"number"},"portalKey":{"$id":"/properties/portalKey","description":"Portal's slug/key","type":"string"},"portalOrigin":{"$id":"/properties/portalOrigin","description":"Portal's URL","type":"string"},"portalTitle":{"$id":"/properties/portalTitle","description":"Portal slug/key","type":"string"},"updateSource":{"$id":"/properties/updateSource","description":"the method in which the status was updated","enum":["edi_api","edi_sftp","extension","rpa","status_checker","web_app"],"type":"string"}},"required":["institutionId","internalAuthId","newStatus","oldStatus"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA Status Updated', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaStepCompleted' track call.
 *
 * @param PaStepCompleted props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paStepCompleted(props: PaStepCompleted, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_Step_Completed","$schema":"http://json-schema.org/draft-07/schema#","description":"User completes a step in the PA process","properties":{"accountId":{"$id":"/properties/accountId","description":"ID associated with user account","type":"number"},"email":{"$id":"/properties/email","description":"email associated with user account","type":"string"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"boolean field for whether the institution is identified as a top practice by SamaCare sales","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"name of the practice the user is associated with","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"describes the medical speciality of the practice ","type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"isAdmin":{"$id":"/properties/isAdmin","description":"boolean field for whether user role is admin or not","type":"boolean"},"paStep":{"$id":"/properties/paStep","description":"denotes which step in the PA process a user completed","enum":["caremetx_enrollment_and_submit","enrollment_and_submit","form_details","initiate_prior_authorization","lomn_edit","lomn_fill","patient_and_insurance","provider_and_location","request_details","review_sign_and_submit","service_and_diagnosis"],"type":"string"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"}},"required":["institutionName","internalAuthId","isAdmin","accountId","email","institutionIsTop"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA Step Completed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PaViewed' track call.
 *
 * @param PaViewed props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function paViewed(props: PaViewed, options?: Options, callback?: Callback): void {

    const schema = {"$id":"PA_Viewed","$schema":"http://json-schema.org/draft-07/schema#","description":"User views an individual PA's details.","properties":{"_":{"type":"null","$id":"/properties/_","description":"This property is added as a workaround to generate type for events without properties"}},"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('PA Viewed', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PasswordResetRequested' track call.
 *
 * @param PasswordResetRequested props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function passwordResetRequested(props: PasswordResetRequested, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Password_Reset_Requested","$schema":"http://json-schema.org/draft-07/schema#","properties":{"email":{"$id":"/properties/email","description":"The email address requesting a password reset.","pattern":"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$\n","type":"string"}},"required":["email"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Password Reset Requested', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'PortalStatusChecked' track call.
 *
 * @param PortalStatusChecked props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function portalStatusChecked(props: PortalStatusChecked, options?: Options, callback?: Callback): void {

    const schema = {"$id":"Portal_Status_Checked","$schema":"http://json-schema.org/draft-07/schema#","description":"Fires when a user selects \"Check Portal Status\"","properties":{"accountId":{"$id":"/properties/accountId","description":"ID associated with user account","type":"number"},"authorizationCorrespondenceCodes":{"$id":"/properties/authorizationCorrespondenceCodes","description":"ID that may change from preliminary ID","type":"array"},"email":{"$id":"/properties/email","description":"email associated with user account","type":"string"},"hcpcsCodes":{"$id":"/properties/hcpcsCodes","description":"code used to identify health services rendered","type":"array"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"boolean field for whether the institution is identified as a top practice by SamaCare sales","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"name of the practice the user is associated with","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"describes the medical speciality of the practice ","type":"string"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"string"},"insuranceGroupNumber":{"$id":"/properties/insuranceGroupNumber","description":"","type":"string"},"insuranceMemberId":{"$id":"/properties/insuranceMemberId","description":"","type":"string"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"insuranceState":{"$id":"/properties/insuranceState","description":"state in which the patient insurance plan is for","type":"string"},"insuranceType":{"$id":"/properties/insuranceType","description":"type of insurance plan","enum":["commercial","medicaid","medicare_advantage_or_replacement","other"],"type":"string"},"internalAuthId":{"$id":"/properties/internalAuthId","description":"ID of the PA","type":"number"},"isAdmin":{"$id":"/properties/isAdmin","description":"boolean field for whether user role is admin or not","type":"boolean"},"paType":{"$id":"/properties/paType","description":"","enum":["edi","enrollment","external","form","multi_party","portal","portal_external","referral"],"type":"string"},"portalAuthorizationId":{"$id":"/properties/portalAuthorizationId","description":"payer provided preliminary identifier","type":"string"}},"required":["isAdmin","accountId","email","institutionIsTop","institutionName","internalAuthId"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('Portal Status Checked', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'UserLoggedOut' track call.
 *
 * @param UserLoggedOut props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function userLoggedOut(props: UserLoggedOut, options?: Options, callback?: Callback): void {

    const schema = {"$id":"User_Logged_Out","$schema":"http://json-schema.org/draft-07/schema#","properties":{"_":{"type":"null","$id":"/properties/_","description":"This property is added as a workaround to generate type for events without properties"}},"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('User Logged Out', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'UserSignedIn' track call.
 *
 * @param UserSignedIn props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function userSignedIn(props: UserSignedIn, options?: Options, callback?: Callback): void {

    const schema = {"$id":"User_Signed_In","$schema":"http://json-schema.org/draft-07/schema#","description":"User selects \"Login\" after entering their credentials for SamaCare","properties":{"accountId":{"$id":"/properties/accountId","description":"ID associated with user account","type":"number"},"email":{"$id":"/properties/email","description":"email associated with user account","type":"string"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"boolean field for whether the institution is identified as a top practice by SamaCare sales","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"name of the practice the user is associated with","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"describes the medical speciality of the practice ","type":"string"},"isAdmin":{"$id":"/properties/isAdmin","description":"boolean field for whether user role is admin or not","type":"boolean"},"lastLoginTimestamp":{"$id":"/properties/lastLoginTimestamp","description":"","type":"string"}},"required":["isAdmin","accountId","email","institutionIsTop","institutionName"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('User Signed In', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ViewBvLite' track call.
 *
 * @param ViewBvLite props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function viewBvLite(props: ViewBvLite, options?: Options, callback?: Callback): void {

    const schema = {"$id":"View_Bv_Lite","$schema":"http://json-schema.org/draft-07/schema#","description":"User view BV Lite details either from PA workflow or from BV grid","properties":{"accountId":{"$id":"/properties/accountId","description":"ID associated with user account","type":"number"},"email":{"$id":"/properties/email","description":"email associated with user account","type":"string"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"boolean field for whether the institution is identified as a top practice by SamaCare sales","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"name of the practice the user is associated with","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"describes the medical speciality of the practice ","type":"string"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"string"},"insuranceGroupNumber":{"$id":"/properties/insuranceGroupNumber","description":"","type":"string"},"insuranceMemberId":{"$id":"/properties/insuranceMemberId","description":"","type":"string"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"insuranceState":{"$id":"/properties/insuranceState","description":"state in which the patient insurance plan is for","type":"string"},"insuranceType":{"$id":"/properties/insuranceType","description":"type of insurance plan","enum":["commercial","medicaid","medicare_advantage_or_replacement","other"],"type":"string"},"isAdmin":{"$id":"/properties/isAdmin","description":"boolean field for whether user role is admin or not","type":"boolean"},"viewSource":{"$id":"/properties/viewSource","description":"where does the user view BV lite","enum":["bv_grid","pa"],"type":"string"}},"required":["institutionName","isAdmin","viewSource","accountId","email","institutionIsTop"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('View Bv Lite', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ViewFullBv' track call.
 *
 * @param ViewFullBv props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function viewFullBv(props: ViewFullBv, options?: Options, callback?: Callback): void {

    const schema = {"$id":"View_full_BV","$schema":"http://json-schema.org/draft-07/schema#","description":"User view full BV details either from PA workflow or from BV grid","properties":{"accountId":{"$id":"/properties/accountId","description":"ID associated with user account","type":"number"},"email":{"$id":"/properties/email","description":"email associated with user account","type":"string"},"institutionIsTop":{"$id":"/properties/institutionIsTop","description":"boolean field for whether the institution is identified as a top practice by SamaCare sales","type":"boolean"},"institutionName":{"$id":"/properties/institutionName","description":"name of the practice the user is associated with","type":"string"},"institutionSpecialty":{"$id":"/properties/institutionSpecialty","description":"describes the medical speciality of the practice ","type":"string"},"insuranceCompanyId":{"$id":"/properties/insuranceCompanyId","description":"ID associated to the insurance company","type":"string"},"insuranceGroupNumber":{"$id":"/properties/insuranceGroupNumber","description":"","type":"string"},"insuranceMemberId":{"$id":"/properties/insuranceMemberId","description":"","type":"string"},"insuranceName":{"$id":"/properties/insuranceName","description":"name of patient insurance company","type":"string"},"insuranceState":{"$id":"/properties/insuranceState","description":"state in which the patient insurance plan is for","type":"string"},"insuranceType":{"$id":"/properties/insuranceType","description":"type of insurance plan","enum":["commercial","medicaid","medicare_advantage_or_replacement","other"],"type":"string"},"isAdmin":{"$id":"/properties/isAdmin","description":"boolean field for whether user role is admin or not","type":"boolean"},"viewSource":{"$id":"/properties/viewSource","description":"where does the user view BV lite","enum":["bv_grid","pa"],"type":"string"}},"required":["accountId","email","institutionIsTop","institutionName","isAdmin"],"type":"object"};
    validateAgainstSchema(props, schema);

    const a = analytics();
    if (a) {
        a.track('View full BV', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}

const clientAPI = {
    /**
     * Updates the run-time configuration of this Typewriter client.
     *
     * @param {TypewriterOptions} options - the options to upsert
     *
     * @typedef {Object} TypewriterOptions
     * @property {AnyAnalytics} [analytics] - Underlying analytics instance where analytics
     * 		calls are forwarded on to. Defaults to window.analytics.
     * @property {Function} [onViolation] - Handler fired when if an event does not match its spec. This handler does not fire in
     * 		production mode, because it requires inlining the full JSON Schema spec for each event in your Tracking Plan. By default,
     * 		it will throw errors if NODE_ENV="test" so that tests will fail if a message does not match the spec. Otherwise, errors
     * 		will be logged to stderr.
     */
    setTypewriterOptions,

    /**
     * Fires a 'AccountCreated' track call.
     *
     * @param AccountCreated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    accountCreated,
    /**
     * Fires a 'AccountDeactivated' track call.
     *
     * @param AccountDeactivated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    accountDeactivated,
    /**
     * Fires a 'AccountUnlocked' track call.
     *
     * @param AccountUnlocked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    accountUnlocked,
    /**
     * Fires a 'ExtensionAutofillAttempt' track call.
     *
     * @param ExtensionAutofillAttempt props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    extensionAutofillAttempt,
    /**
     * Fires a 'ExtensionAutofillFieldDetected' track call.
     *
     * @param ExtensionAutofillFieldDetected props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    extensionAutofillFieldDetected,
    /**
     * Fires a 'ExtensionAutofillOverridden' track call.
     *
     * @param ExtensionAutofillOverridden props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    extensionAutofillOverridden,
    /**
     * Fires a 'ExtensionFieldAutofilled' track call.
     *
     * @param ExtensionFieldAutofilled props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    extensionFieldAutofilled,
    /**
     * Fires a 'ExtensionFormViewed' track call.
     *
     * @param ExtensionFormViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    extensionFormViewed,
    /**
     * Fires a 'ExtensionInputFound' track call.
     *
     * @param ExtensionInputFound props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    extensionInputFound,
    /**
     * Fires a 'ExtensionOpened' track call.
     *
     * @param ExtensionOpened props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    extensionOpened,
    /**
     * Fires a 'ExtensionSaveToSamaCareButtonClicked' track call.
     *
     * @param ExtensionSaveToSamaCareButtonClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    extensionSaveToSamaCareButtonClicked,
    /**
     * Fires a 'ExtensionViewAuthorizationButtonClicked' track call.
     *
     * @param ExtensionViewAuthorizationButtonClicked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    extensionViewAuthorizationButtonClicked,
    /**
     * Fires a 'InitiateBvLite' track call.
     *
     * @param InitiateBvLite props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    initiateBvLite,
    /**
     * Fires a 'InstitutionCreated' track call.
     *
     * @param InstitutionCreated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    institutionCreated,
    /**
     * Fires a 'IntegrationCreated' track call.
     *
     * @param IntegrationCreated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    integrationCreated,
    /**
     * Fires a 'PaDeleted' track call.
     *
     * @param PaDeleted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paDeleted,
    /**
     * Fires a 'PaExternalMatchFailed' track call.
     *
     * @param PaExternalMatchFailed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paExternalMatchFailed,
    /**
     * Fires a 'PaExternalMatched' track call.
     *
     * @param PaExternalMatched props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paExternalMatched,
    /**
     * Fires a 'PaExternalSearchCompleted' track call.
     *
     * @param PaExternalSearchCompleted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paExternalSearchCompleted,
    /**
     * Fires a 'PaExternalSearchPolled' track call.
     *
     * @param PaExternalSearchPolled props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paExternalSearchPolled,
    /**
     * Fires a 'PaExternalSearchProcessFailed' track call.
     *
     * @param PaExternalSearchProcessFailed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paExternalSearchProcessFailed,
    /**
     * Fires a 'PaExternalSearchRequestFailed' track call.
     *
     * @param PaExternalSearchRequestFailed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paExternalSearchRequestFailed,
    /**
     * Fires a 'PaExternalSearchRequestSucceeded' track call.
     *
     * @param PaExternalSearchRequestSucceeded props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paExternalSearchRequestSucceeded,
    /**
     * Fires a 'PaExternalSearchRequested' track call.
     *
     * @param PaExternalSearchRequested props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paExternalSearchRequested,
    /**
     * Fires a 'PaExternalSearchZeroMatches' track call.
     *
     * @param PaExternalSearchZeroMatches props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paExternalSearchZeroMatches,
    /**
     * Fires a 'PaInitiated' track call.
     *
     * @param PaInitiated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paInitiated,
    /**
     * Fires a 'PaListViewed' track call.
     *
     * @param PaListViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paListViewed,
    /**
     * Fires a 'PaStatusCheckEligible' track call.
     *
     * @param PaStatusCheckEligible props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paStatusCheckEligible,
    /**
     * Fires a 'PaStatusInquired' track call.
     *
     * @param PaStatusInquired props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paStatusInquired,
    /**
     * Fires a 'PaStatusInquiryFailed' track call.
     *
     * @param PaStatusInquiryFailed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paStatusInquiryFailed,
    /**
     * Fires a 'PaStatusUpdated' track call.
     *
     * @param PaStatusUpdated props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paStatusUpdated,
    /**
     * Fires a 'PaStepCompleted' track call.
     *
     * @param PaStepCompleted props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paStepCompleted,
    /**
     * Fires a 'PaViewed' track call.
     *
     * @param PaViewed props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    paViewed,
    /**
     * Fires a 'PasswordResetRequested' track call.
     *
     * @param PasswordResetRequested props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    passwordResetRequested,
    /**
     * Fires a 'PortalStatusChecked' track call.
     *
     * @param PortalStatusChecked props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    portalStatusChecked,
    /**
     * Fires a 'UserLoggedOut' track call.
     *
     * @param UserLoggedOut props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    userLoggedOut,
    /**
     * Fires a 'UserSignedIn' track call.
     *
     * @param UserSignedIn props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    userSignedIn,
    /**
     * Fires a 'ViewBvLite' track call.
     *
     * @param ViewBvLite props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    viewBvLite,
    /**
     * Fires a 'ViewFullBv' track call.
     *
     * @param ViewFullBv props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    viewFullBv,
};

export default new Proxy<typeof clientAPI>(clientAPI, {
    get(target, method) {
        if (typeof method === 'string' && target.hasOwnProperty(method)) {
            return target[method as keyof typeof clientAPI];
        }

        return () => {
            console.warn(`⚠️  You made an analytics call (${String(method)}) that can't be found. Either:
         a) Re-generate your typewriter client: \`npx typewriter\`
         b) Add it to your Tracking Plan: https://app.segment.com/segment-oscb/protocols/tracking-plans/rs_1zTHJU9fd5mt7cndWnd4PgJbMCE`);
            const a = analytics();
            if (a) {
                a.track(
                    'Unknown Analytics Call Fired',
                    {
                        method,
                    },
                    withTypewriterContext(),
                );
            }
        };
    },
});

